/**
 * PluginBridge — Host-side bridge for communicating with plugin iframes.
 *
 * Handles postMessage communication, permission enforcement,
 * rate limiting, and proxying requests to the FIAS backend.
 */

import { invokeClientEntity, getClientEntityRegistration } from '@/lib/client-entity-registry';
import {
  entityInvokeRequiredPermission,
  INTEGRATION_ENTITY_PERMISSIONS,
  PLUGIN_STORAGE_ENTITY_ID,
  PLUGIN_DATASTORE_ENTITY_ID,
  PLUGIN_WORKSPACES_ENTITY_ID,
  PLUGIN_STORE_ENTITY_ID,
  VAULT_USER_DOCUMENTS_ENTITY_ID,
  VAULT_ARCHE_DOCUMENTS_ENTITY_ID,
} from '@/lib/integration-entity-permissions';
import { PluginSubscriptionManager } from './plugin-subscription-manager';
import { getWebSocket } from '@/lib/websocket';
import { WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { attachCsrfHeader } from '@/lib/csrf';
// Plugin permission scopes. Imported type-only (erased at build) from the
// SDK's generated permissions — the client-safe mirror of @fias/db-types,
// generated by `pnpm generate:sdk-types` and pinned in lockstep by
// tests/architecture/sdk-permissions-match-db-types.test.ts. Importing rather
// than re-declaring the union here avoids drift: a prior hand-maintained copy
// silently fell behind db-types. A type-only import adds no runtime dependency
// on the server-only db-types package (the bridge ships in plugin iframes).
import type { PluginPermission, FiasVendoredAssets } from '@fias/arche-sdk';
import type {
  PluginDeclaredAiAction,
  PluginRuntimeAiDeclaration,
} from '@/components/fias-ai/types';

/**
 * Arche id formats accepted by the `open_arche` op. The regex mirrors
 * `isValidArcheId` in `@fias/db-types` (`arche.ts`) — inlined rather than
 * imported because this bridge takes no runtime dependency on that
 * server-only package.
 */
function isValidArcheIdFormat(id: string): boolean {
  return /^arc_[a-f0-9]{32}$/.test(id) || /^arche_[a-z0-9_]+$/.test(id);
}

// Bridge message types (must match the SDK's types)
interface BridgeMessage {
  type: string;
  messageId: string;
  payload: unknown;
}

/**
 * Bridge protocol version negotiation — host side.
 *
 *   HOST_BRIDGE_PROTOCOL_VERSION
 *     The protocol version this host SPEAKS. Bumped on backward-
 *     incompatible additions to the message shape.
 *
 *   HOST_REQUIRES_SDK_PROTOCOL_VERSION
 *     Minimum SDK protocol version this host accepts. If a plugin
 *     reports a lower version in its `ready` message, the host fires
 *     `onIncompatibleSdk` so the UI can render a "plugin needs update"
 *     affordance instead of letting the plugin call ops the host has
 *     removed.
 *
 * SYNC NOTE: must rise in lockstep with
 * packages/arche-sdk/src/protocol.ts when the protocol changes.
 */
const HOST_BRIDGE_PROTOCOL_VERSION = 1;
const HOST_REQUIRES_SDK_PROTOCOL_VERSION = 1;

/**
 * Max time the host waits for a plugin to return its state snapshot after a
 * `preview_state_capture` request. Plugins that support the protocol reply
 * within a tick (the SDK handler is synchronous), so this only bounds the
 * pathological case; it never delays plugins that don't advertise support
 * (those skip capture entirely).
 */
const PREVIEW_STATE_CAPTURE_TIMEOUT_MS = 250;

/**
 * Cap on the serialized size of a captured preview-state snapshot. Mirrors the
 * defensive posture of the preview storage budget — a buggy plugin returning a
 * huge object shouldn't be ferried across the reload. Oversized snapshots are
 * dropped (logged), so the worst case is a non-restored report, not an OOM.
 */
const PREVIEW_STATE_MAX_BYTES = 512 * 1024;

// ── Fias AI action declaration (`ai_actions_declare` / `ai_state` lane) ──────
// This lane is fire-and-forget metadata registration (same family as resize/
// toast), which means checkRateLimit never sees it — every cap is enforced
// right here. Bounds mirror `MANIFEST_FIAS_AI_LIMITS` in @fias/db-types
// (the client cannot import db-types at runtime, so the numbers are
// duplicated deliberately; server-side truth is the assistant turn schema).
/** Max actions one runtime declaration may carry (= the turn schema's cap). */
const AI_ACTIONS_MAX_COUNT = 50;
/** Serialized cap on a whole declaration — action text enters the model's tool list. */
const AI_ACTIONS_MAX_DECLARATION_BYTES = 64 * 1024;
/**
 * Re-declaration window (unmetered lane). VALID declarations inside the
 * window are trailing-edge coalesced — held, latest wins, applied at the
 * window edge — never dropped: the content-diffed SDK won't re-send one.
 */
const AI_ACTIONS_MIN_REDECLARE_INTERVAL_MS = 1_000;

/** `ai_open` debounce — a plugin may request the assistant panel at most once
 * per window. The renderer layers a longer cooldown after a USER-close on top
 * (the bridge cannot see panel state), so this bound only has to stop bursts. */
const AI_OPEN_MIN_INTERVAL_MS = 10_000;
/**
 * ai_state coalescing window (trailing edge, latest wins). Each accepted
 * snapshot re-renders the host, so a plugin pushing per-frame must not
 * drive the host at frame rate.
 */
const AI_STATE_MIN_INTERVAL_MS = 250;
/**
 * Serialized cap on an `ai_state` snapshot. Deliberately small: the server
 * does NOT bound `archeContext.state` and JSON-stringifies it into the system
 * prompt on EVERY turn — an unbounded snapshot silently balloons the user's
 * token bill. Oversized snapshots are dropped (logged); the last good one
 * stays current.
 */
const AI_STATE_MAX_BYTES = 16 * 1024;
/**
 * How long a dispatched ai_action may run in the plugin before the bridge
 * rejects it. Below the assistant loop's own applyAction timeout (30s) so the
 * failure carries this precise message rather than a generic race timeout.
 */
const AI_ACTION_DISPATCH_TIMEOUT_MS = 25_000;
const AI_ACTION_ID_REGEX = /^[a-z][a-z0-9_]*$/;
/** = MANIFEST_FIAS_AI_LIMITS.MAX_PARAMETER_SCHEMA_BYTES — lanes must agree. */
const AI_ACTION_MAX_PARAMETER_SCHEMA_BYTES = 4096;
/** = MANIFEST_FIAS_AI_LIMITS.MAX_AVAILABLE_WHEN_KEYS — lanes must agree. */
const AI_ACTION_MAX_AVAILABLE_WHEN_KEYS = 8;
/**
 * C0 controls + DEL, Unicode bidi controls, and zero-width/format chars —
 * prompt-injection + text-spoofing vectors. Mirrors CONTROL_CHARS_REGEX in
 * @fias/db-types (lockstep-pinned by the fias-ai bounds arch test).
 */
const AI_CONTROL_CHARS_REGEX =
  // eslint-disable-next-line no-control-regex -- matching control chars IS this guard's job
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/;

type ParseResult<T> = { value: T } | { error: string };

/**
 * Deep-scan every string (keys and values) in a JSON-shaped payload for
 * banned characters. NOT a serialized-string scan: JSON.stringify ESCAPES
 * C0 controls (ESC becomes backslash-u text), so scanning the serialization
 * silently misses exactly the classic injection bytes.
 */
function containsBannedChars(value: unknown): boolean {
  if (typeof value === 'string') return AI_CONTROL_CHARS_REGEX.test(value);
  if (Array.isArray(value)) return value.some(containsBannedChars);
  if (value && typeof value === 'object') {
    return Object.entries(value).some(
      ([k, v]) => AI_CONTROL_CHARS_REGEX.test(k) || containsBannedChars(v),
    );
  }
  return false;
}

/**
 * Parse-and-build one runtime-declared action. Everything here reaches the
 * model's tool list verbatim, so this is the trust boundary between
 * iframe-supplied text and the assistant. Constructs the typed action from
 * the VALIDATED fields (never a cast of the raw payload) so a field the
 * validator doesn't know about cannot ride along.
 */
function parseRuntimeAiAction(entry: unknown): ParseResult<PluginDeclaredAiAction> {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return { error: 'action must be an object' };
  }
  const a = entry as Record<string, unknown>;
  if (typeof a.id !== 'string' || a.id.length > 100 || !AI_ACTION_ID_REGEX.test(a.id)) {
    return { error: `invalid action id ${JSON.stringify(a.id)}` };
  }
  if (
    typeof a.description !== 'string' ||
    a.description.trim().length === 0 ||
    a.description.length > 1000 ||
    AI_CONTROL_CHARS_REGEX.test(a.description)
  ) {
    return { error: `action "${a.id}": invalid description` };
  }
  if (
    !a.parameterSchema ||
    typeof a.parameterSchema !== 'object' ||
    Array.isArray(a.parameterSchema)
  ) {
    return { error: `action "${a.id}": parameterSchema must be an object` };
  }
  const serializedSchema = JSON.stringify(a.parameterSchema);
  // Same per-action bound the manifest lane enforces (MANIFEST_FIAS_AI_LIMITS
  // .MAX_PARAMETER_SCHEMA_BYTES) — the two lanes must not diverge on what a
  // schema may weigh, since both land verbatim in the model's tool list.
  if (serializedSchema.length > AI_ACTION_MAX_PARAMETER_SCHEMA_BYTES) {
    return {
      error: `action "${a.id}": parameterSchema must serialize to ≤ ${AI_ACTION_MAX_PARAMETER_SCHEMA_BYTES} bytes`,
    };
  }
  if (containsBannedChars(a.parameterSchema)) {
    return { error: `action "${a.id}": parameterSchema contains control characters` };
  }
  if (a.requiresConfirmation !== undefined && typeof a.requiresConfirmation !== 'boolean') {
    return { error: `action "${a.id}": requiresConfirmation must be a boolean` };
  }
  if (
    a.category !== undefined &&
    (typeof a.category !== 'string' ||
      a.category.length > 100 ||
      AI_CONTROL_CHARS_REGEX.test(a.category))
  ) {
    return { error: `action "${a.id}": invalid category` };
  }
  let availableWhen: Record<string, boolean | string> | undefined;
  if (a.availableWhen !== undefined) {
    if (!a.availableWhen || typeof a.availableWhen !== 'object' || Array.isArray(a.availableWhen)) {
      return { error: `action "${a.id}": availableWhen must be an object` };
    }
    const availableWhenEntries = Object.entries(a.availableWhen as Record<string, unknown>);
    // = MANIFEST_FIAS_AI_LIMITS.MAX_AVAILABLE_WHEN_KEYS — lanes must agree.
    if (availableWhenEntries.length > AI_ACTION_MAX_AVAILABLE_WHEN_KEYS) {
      return {
        error: `action "${a.id}": availableWhen must declare ≤ ${AI_ACTION_MAX_AVAILABLE_WHEN_KEYS} keys`,
      };
    }
    availableWhen = {};
    for (const [key, value] of availableWhenEntries) {
      if (key.length === 0 || key.length > 64) {
        return { error: `action "${a.id}": invalid availableWhen key` };
      }
      if (typeof value !== 'boolean' && (typeof value !== 'string' || value.length > 100)) {
        return {
          error: `action "${a.id}": availableWhen["${key}"] must be a boolean or short string`,
        };
      }
      availableWhen[key] = value;
    }
  }
  return {
    value: {
      id: a.id,
      description: a.description,
      parameterSchema: a.parameterSchema as Record<string, unknown>,
      ...(a.requiresConfirmation !== undefined
        ? { requiresConfirmation: a.requiresConfirmation as boolean }
        : {}),
      ...(a.category !== undefined ? { category: a.category as string } : {}),
      ...(availableWhen !== undefined ? { availableWhen } : {}),
    },
  };
}

/**
 * Parse-and-build a whole runtime declaration. Atomic: one invalid action
 * rejects the declaration (mirrors the manifest validator — a partial tool
 * list is worse than none).
 */
function parseRuntimeAiDeclaration(
  payload: unknown,
): { declaration: PluginRuntimeAiDeclaration } | { error: string } {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { error: 'payload must be an object or null' };
  }
  const decl = payload as Record<string, unknown>;
  if (
    decl.description !== undefined &&
    (typeof decl.description !== 'string' ||
      decl.description.length > 2000 ||
      AI_CONTROL_CHARS_REGEX.test(decl.description))
  ) {
    return { error: 'invalid description' };
  }
  if (!Array.isArray(decl.actions) || decl.actions.length === 0) {
    return { error: 'actions must be a non-empty array' };
  }
  if (decl.actions.length > AI_ACTIONS_MAX_COUNT) {
    return { error: `${decl.actions.length} actions exceeds the ${AI_ACTIONS_MAX_COUNT} cap` };
  }
  let serializedBytes = 0;
  try {
    serializedBytes = JSON.stringify(decl).length;
  } catch {
    return { error: 'payload not serializable' };
  }
  if (serializedBytes > AI_ACTIONS_MAX_DECLARATION_BYTES) {
    return {
      error: `${serializedBytes} bytes exceeds the ${AI_ACTIONS_MAX_DECLARATION_BYTES}-byte cap`,
    };
  }
  const seen = new Set<string>();
  const actions: PluginDeclaredAiAction[] = [];
  for (const entry of decl.actions) {
    const parsed = parseRuntimeAiAction(entry);
    if ('error' in parsed) return parsed;
    if (seen.has(parsed.value.id)) {
      return { error: `duplicate action id "${parsed.value.id}"` };
    }
    seen.add(parsed.value.id);
    actions.push(parsed.value);
  }
  return {
    declaration: {
      ...(typeof decl.description === 'string' ? { description: decl.description } : {}),
      actions,
    },
  };
}

interface PluginBridgeOptions {
  archId: string;
  permissions: PluginPermission[];
  iframe: HTMLIFrameElement;
  onResize?: (height: number) => void;
  onToast?: (message: string, variant: string) => void;
  /**
   * Runtime Fias AI action declaration from the plugin (`ai_actions_declare`).
   * Fires only after the bridge's permission + bounds checks pass; `null`
   * clears a previous declaration. The HOST decides whether to honour it —
   * the runtime lane is gated on `arches.is_trusted`, which the renderer
   * holds and the bridge does not.
   */
  onAiActionsDeclare?: (declaration: PluginRuntimeAiDeclaration | null) => void;
  /** Plugin state snapshot for the assistant (`ai_state`), already size-capped. */
  onAiState?: (state: Record<string, unknown>) => void;
  /**
   * Plugin asked the host to open the global Fias AI panel (`ai_open`,
   * discoverability Part 2). Fires only after the permission gate and the
   * bridge-level debounce; the renderer applies its own after-user-close
   * cooldown before actually opening, so a plugin can never nag the panel
   * back open against the user's wishes.
   */
  onAiOpenRequest?: () => void;
  onNavigate?: (path: string) => void;
  /**
   * Cross-arche navigation requested via the `open_arche` op. `archeId` is
   * already format-validated by the bridge; the host resolves it to
   * `/a/<archeId>` and navigates (same-tab) or, when `newTab` is set,
   * best-effort opens a new tab.
   */
  onOpenArche?: (archeId: string, newTab: boolean) => void;
  getTheme: () => unknown;
  /** Self-hosted vendored-library asset URLs (keyed by import specifier) for the
   *  init payload. Called only when the plugin declares
   *  `sandbox:vendored-libraries`; returns null when the CDN is unconfigured. */
  getVendoredAssets?: () => FiasVendoredAssets | null;
  getUser: () => Promise<{ userId: string; displayName: string; avatar: string | null }>;
  bridgeApiUrl: string;
  getAuthToken: () => Promise<string>;
  /**
   * Cache-bust hook for expired capability tokens: invoked once when a
   * bridge call comes back BRIDGE_TOKEN_EXPIRED, immediately before the
   * single retry re-invokes getAuthToken(). Without it the retry would read
   * the same stale token back out of the caller's cache.
   */
  onAuthTokenExpired?: () => void;
  /**
   * When true, the bridge is hosting the Arche Builder preview. AI/image ops
   * route through the session-scoped hooks below; store/IAP stays simulated
   * and vault/surface/assets stay unsupported (see the per-case branches);
   * everything else (storage/data/workspaces) proxies to the LIVE plugins
   * bridge under the `preview_<sessionId>` sandbox tenant — production
   * behavior, production errors (ADR-012 Phase M3).
   */
  previewMode?: boolean;
  /** Custom entity_invoke handler for preview (avoids backend proxy for entity lookup). */
  onEntityInvoke?: (payload: {
    entityId: string | Record<string, unknown>;
    input: string;
    systemPrompt?: string;
    /** Vision inputs — forwarded to the preview ai-invoke route (parity with published entity_invoke). */
    images?: Array<{ mediaType: string; dataBase64: string }>;
  }) => Promise<{ output: string }>;
  /** Custom image_generate handler for preview (avoids backend proxy for image config lookup). */
  onImageGenerate?: (payload: {
    entityId: string | Record<string, unknown>;
    prompt: string;
    size?: string;
    quality?: string;
    style?: string;
  }) => Promise<{
    imageUrl: string;
    imageRef: string;
    revisedPrompt?: string;
    provider: string;
    model: string;
  }>;
  /** Callback for bridge-level log events (e.g., burst detection warnings). */
  onLog?: (level: 'warn' | 'error', message: string) => void;
  /**
   * Fired when the SDK acknowledges init with a `sdkProtocolVersion`
   * lower than `HOST_REQUIRES_SDK_PROTOCOL_VERSION`. The host UI should
   * unmount the iframe and render a "plugin needs update" affordance.
   * Receives the actual SDK version the plugin reported.
   */
  onIncompatibleSdk?: (sdkProtocolVersion: number, minRequired: number) => void;
  /**
   * Fired when a retired op-family alias (storage_*, data_*, workspace_*, store_*)
   * is received from an old, immutable published bundle (ADR-012 Phase L Job 3
   * Step 2). The host UI should render a "plugin needs an update" affordance.
   * Receives the retired op name.
   */
  onRetiredCapability?: (op: string) => void;
  /**
   * Callback for IAP purchase requests. When set, store_purchase messages
   * invoke this instead of proxying directly to backend. The callback should
   * show a confirmation UI and resolve with the backend response on confirm,
   * or { success: false, error: 'USER_CANCELED' } on cancel.
   */
  onPurchaseRequest?: (product: {
    productIdentifier: string;
    name: string;
    description: string | null;
    priceCredits: number;
    type: string;
  }) => Promise<unknown>;
  /**
   * Callback for the Vault user-document picker (host-UI mediation, the
   * purchase-sheet pattern). When set, `vault_documents_pick` messages open
   * the host's picker + consent sheet; the plugin receives ONLY what the
   * user hand-picked and confirmed — metadata, never bytes (content flows
   * through the audited consented-read entity verbs afterwards). Resolve
   * with { canceled: true } on dismiss.
   */
  onVaultPickRequest?: (request: { maxDocuments: number }) => Promise<
    | { canceled: true }
    | {
        documents: Array<{
          documentId: string;
          name: string;
          mimeType: string | null;
          sizeBytes: number | null;
        }>;
      }
  >;
  /** Mock store products for preview mode. */
  previewStoreProducts?: Array<{
    productIdentifier: string;
    type: string;
    name: string;
    description: string | null;
    priceCredits: number;
    metadata: Record<string, unknown>;
    subscriptionPeriod?: string;
  }>;
  /**
   * Preview-mode only. A plugin state snapshot captured from the previous
   * iframe just before this (re)load — see `captureState()`. When the freshly
   * loaded plugin signals readiness and advertises preview-state support, the
   * bridge replays this snapshot so transient UI state (e.g. a generated
   * report) survives a rebuild. Ignored outside preview mode.
   */
  initialPreviewState?: unknown;
  /** Fired after `initialPreviewState` has been sent to the plugin for restore. */
  onPreviewStateRestored?: () => void;
}

/**
 * Rate limiting state per message type.
 */
interface RateLimitBucket {
  count: number;
  windowStart: number;
}

const RATE_LIMITS: Record<string, { maxPerMinute: number }> = {
  // Backstop against popup-bombing — a plugin that requests newTab opens
  // could otherwise spawn tabs in a loop. Tile clicks are well under this.
  open_arche: { maxPerMinute: 30 },
  entity_invoke: { maxPerMinute: 60 },
  client_entity_invoke: { maxPerMinute: 60 },
  image_generate: { maxPerMinute: 10 },
  audio_generate: { maxPerMinute: 10 },
  storage_write: { maxPerMinute: 120 },
  storage_read: { maxPerMinute: 300 },
  storage_list: { maxPerMinute: 60 },
  storage_delete: { maxPerMinute: 60 },
  data_put: { maxPerMinute: 120 },
  data_get: { maxPerMinute: 300 },
  data_query: { maxPerMinute: 60 },
  data_delete: { maxPerMinute: 60 },
  data_batch: { maxPerMinute: 30 },
  data_create_collection: { maxPerMinute: 10 },
  data_delete_collection: { maxPerMinute: 10 },
  data_list_collections: { maxPerMinute: 60 },
  // Workspace tenancy (ADR-010) — mirrors server-side DATA_STORE_RATE_LIMITS.
  workspace_create: { maxPerMinute: 20 },
  workspace_list: { maxPerMinute: 120 },
  workspace_get: { maxPerMinute: 120 },
  workspace_archive: { maxPerMinute: 20 },
  workspace_list_members: { maxPerMinute: 120 },
  workspace_add_member: { maxPerMinute: 30 },
  workspace_update_member: { maxPerMinute: 30 },
  workspace_remove_member: { maxPerMinute: 30 },
  store_get_products: { maxPerMinute: 60 },
  store_purchase: { maxPerMinute: 10 },
  store_get_entitlements: { maxPerMinute: 60 },
  store_get_purchase_history: { maxPerMinute: 30 },
  store_restore: { maxPerMinute: 10 },
  store_cancel_subscription: { maxPerMinute: 10 },
  // Host picker: user-gesture bound anyway; the cap only stops a broken
  // plugin from re-opening the sheet in a loop.
  vault_documents_pick: { maxPerMinute: 5 },
  vault_documents_list: { maxPerMinute: 120 },
  vault_documents_read: { maxPerMinute: 300 },
  // Batch reads count as ONE bridge call. Per-doc rate-limiting on the
  // batch payload (max 50 ids per call) is enforced server-side.
  vault_documents_read_many: { maxPerMinute: 60 },
  vault_documents_download_url: { maxPerMinute: 300 },
  vault_documents_get_urls: { maxPerMinute: 60 },
  vault_documents_write: { maxPerMinute: 60 },
  // Composite binary upload (init→PUT→finalize host-side) — heavier than a
  // text write, so a tighter cap. Backend quota still applies (defense-in-depth).
  vault_documents_upload: { maxPerMinute: 30 },
  vault_documents_update: { maxPerMinute: 60 },
  vault_documents_delete: { maxPerMinute: 30 },
  arche_assets_list: { maxPerMinute: 600 },
  arche_assets_index: { maxPerMinute: 120 },
  arche_assets_get_url: { maxPerMinute: 300 },
};

/**
 * Per-(entityId, verb) rate limits for the generic `entity_invoke` op (ADR-012
 * Phase L, Job 3). When a plugin built against the migrated SDK reaches
 * storage / datastore / workspaces / store through `entity_invoke`, its per-verb
 * cap must match what the legacy alias op had — otherwise the four families
 * collapse into the single `entity_invoke` bucket (60/min) and e.g. storage
 * reads drop from 300 to 60. Values mirror the alias entries in RATE_LIMITS
 * above (which mirror the server-side DATA_STORE_RATE_LIMITS). Verbs absent
 * here (datastore `search` — which had no client alias cap — and `subscribe`,
 * plus every AI/text entity) fall back to the generic `entity_invoke` cap.
 */
/**
 * Per-verb rate WEIGHTS (ADR-016 Phase A): a listed verb consumes one unit of
 * its per-minute budget per started `opsPerUnit` operations in its payload,
 * instead of a flat one unit per call. Config-shaped (entity → verb →
 * divisor) rather than a hardcoded entity-ID conditional, so a second
 * weighted verb is a map entry, not another branch. Mirrors
 * DATASTORE_LIMITS.BATCH_OPS_PER_RATE_UNIT server-side; the server's
 * checkDataStoreRateLimit is authoritative — this mirror exists so plugin
 * authors get the friendly local error at the same threshold.
 */
const ENTITY_INVOKE_RATE_WEIGHTS: Record<string, Record<string, number>> = {
  [PLUGIN_DATASTORE_ENTITY_ID]: { batch: 25 },
};

const ENTITY_INVOKE_RATE_LIMITS: Record<string, Record<string, number>> = {
  [PLUGIN_STORAGE_ENTITY_ID]: { read: 300, write: 120, list: 60, delete: 60 },
  [PLUGIN_DATASTORE_ENTITY_ID]: {
    create_collection: 10,
    list_collections: 60,
    delete_collection: 10,
    put: 120,
    get: 300,
    query: 60,
    delete: 60,
    batch: 30,
  },
  [PLUGIN_WORKSPACES_ENTITY_ID]: {
    create: 20,
    list: 120,
    get: 120,
    archive: 20,
    list_members: 120,
    add_member: 30,
    update_member: 30,
    remove_member: 30,
  },
  // Consented USER-vault reads. Mirrors the server: the entity carries a
  // 30/min budget for the family, and `get_consented_document_bytes` — which
  // can serve 15 MB per call — has its own tighter server-side bucket
  // (`vault_consented_bytes`). Without these entries the whole family fell
  // back to the generic 60/min `entity_invoke` bucket, so the client waved
  // through calls the server then refused, which reads to a plugin author as
  // a flaky platform rather than a budget they exceeded.
  [VAULT_USER_DOCUMENTS_ENTITY_ID]: {
    list_consented_documents: 30,
    get_consented_document: 30,
    get_consented_document_url: 30,
    get_consented_document_bytes: 10,
  },
  // Arche-OWNED byte read-back. Mirrors the server's entity-level 10/min
  // (config `rateLimitPerMinute` — this entity's only verb IS the expensive
  // byte-mover), so the host refuses what the server would refuse instead of
  // waving it through to a confusing server-side RATE_LIMIT.
  [VAULT_ARCHE_DOCUMENTS_ENTITY_ID]: {
    get_own_document_bytes: 10,
  },
  [PLUGIN_STORE_ENTITY_ID]: {
    get_products: 60,
    purchase: 10,
    get_entitlements: 60,
    get_purchase_history: 30,
    restore: 10,
    cancel_subscription: 10,
  },
};

/** Burst detection: catches runaway loops (e.g., useEffect calling dataStore.put every render). */
interface BurstBucket {
  count: number;
  windowStart: number;
  /** When set, all calls of this type are blocked until this timestamp. */
  blockedUntil?: number;
}

const BURST_THRESHOLD = 50;
const BURST_WINDOW_MS = 5_000;
const BURST_COOLDOWN_MS = 10_000;

/**
 * Extracts a short diagnostic identifier from a bridge call's payload so that
 * rate-limit and runaway-loop errors can point to the specific path/resource
 * being hammered (e.g. `__state/gameState` when a game loop is spamming
 * `usePersistentState` writes). Returns an empty string for call types where
 * no sensible target exists.
 */
function describeBridgeTarget(type: string, payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const p = payload as Record<string, unknown>;

  // Retired op-family aliases (storage_*/data_*) can still arrive from old,
  // immutable bundles; describe their target so burst/rate-limit logs stay
  // actionable. Uses if/string-compare, NOT `case` labels, so bridge-op-freeze
  // (which scans this file's `case '<op>':` labels) sees no phantom bridge ops.
  if (type === 'storage_read' || type === 'storage_write' || type === 'storage_delete') {
    return typeof p.path === 'string' ? `path: ${p.path}` : '';
  }
  if (type === 'storage_list') {
    return typeof p.prefix === 'string' ? `prefix: ${p.prefix}` : '';
  }
  if (type === 'data_put' || type === 'data_get' || type === 'data_delete') {
    const collection = typeof p.collection === 'string' ? p.collection : '';
    const docId = typeof p.docId === 'string' ? p.docId : '';
    if (collection && docId) return `${collection}/${docId}`;
    return collection ? `collection: ${collection}` : '';
  }
  if (
    type === 'data_query' ||
    type === 'data_create_collection' ||
    type === 'data_delete_collection'
  ) {
    return typeof p.collection === 'string' ? `collection: ${p.collection}` : '';
  }

  switch (type) {
    case 'entity_invoke':
    case 'client_entity_invoke':
      return typeof p.entityId === 'string' ? `entity: ${p.entityId}` : '';
    case 'navigate':
      return typeof p.path === 'string' ? `path: ${p.path}` : '';
    case 'vault_documents_read':
    case 'vault_documents_download_url':
    case 'vault_documents_update':
    case 'vault_documents_delete':
      return typeof p.documentId === 'string' ? `documentId: ${p.documentId}` : '';
    case 'vault_documents_write':
      return typeof p.name === 'string' ? `name: ${p.name}` : '';
    case 'vault_documents_list':
      return typeof p.search === 'string' ? `search: ${p.search}` : '';
    default:
      return '';
  }
}

/**
 * Retired op-family aliases (ADR-012 Phase L Job 3 Step 2). New bundles emit
 * `entity_invoke`; only old, immutable published bundles still emit these bare
 * names. `data_subscribe` (realtime) is NOT retired — excluded via lookahead.
 */
const RETIRED_ALIAS_OP_RE = /^(?:storage_|workspace_|store_|data_(?!subscribe))/;

// Thrown by checkBurst when a call is blocked for runaway-loop reasons. The
// burst path already fires onLog on the first breach and deliberately stays
// silent during cooldown — the outer request-failure catch uses this tag to
// skip its own onLog so we don't double-log the breach and re-log every
// cooldown-blocked call.
class BridgeBurstError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BridgeBurstError';
  }
}

/**
 * Error carrying the backend bridge's machine-readable failure code from a
 * host-side fetch to the iframe-bound error response. `handleMessage`'s catch
 * forwards `code` as `errorCode` on the posted response message, where the
 * SDK reconstructs a `FiasBridgeError` with the same code — so plugin code
 * sees a real Error with `.message` + `.code` instead of the opaque `{}`
 * that `JSON.stringify(new Error(...))` yields (the Rota incident). The wire
 * format stays plain string fields; the Error never crosses postMessage.
 */
class HostBridgeError extends Error {
  code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'HostBridgeError';
    this.code = code;
  }
}

/**
 * Turns an SSE `{ error: … }` frame into a `HostBridgeError`.
 *
 * Two frame shapes are in play. The preview route emits the same structured
 * body as its JSON responses (`{ error: { code, message, requestId, … } }`),
 * so a mid-stream failure keeps its machine-readable code. The published
 * bridge still emits a bare `{ error: "<message>" }` string; it is accepted
 * here so the two readers can share one code path, and so the published frame
 * can gain structure later without a client change.
 */
function hostBridgeErrorFromSseFrame(raw: unknown): HostBridgeError {
  if (raw && typeof raw === 'object') {
    const e = raw as { code?: unknown; message?: unknown };
    return new HostBridgeError(
      typeof e.message === 'string' && e.message ? e.message : 'AI invocation failed',
      typeof e.code === 'string' && e.code ? e.code : 'BRIDGE_ERROR',
    );
  }
  return new HostBridgeError(
    typeof raw === 'string' && raw ? raw : 'AI invocation failed',
    'BRIDGE_ERROR',
  );
}

/**
 * Async-surface poll-fallback cadence (image_generate + surface_invoke).
 *
 * The WebSocket broadcast / late-subscribe replay is the primary, fast path —
 * it resolves the moment the result lands. This poll only covers the case
 * where BOTH the live broadcast was missed AND the subscription was already
 * open when the (now-recoverable) server-side completion happened, so the
 * one-shot replay never re-fires for this connection.
 *
 * Cadence: stay quiet through the typical generation window (no polls on the
 * happy path, where WS wins first), then — if still unresolved — poll on a
 * short interval so a missed broadcast recovers in seconds, not the old 180 s.
 * The server's `request_state` row makes these polls cross-replica durable.
 */
const ASYNC_POLL_GRACE_MS = 15_000; // first poll after the typical gen window
const ASYNC_POLL_INTERVAL_MS = 5_000; // then every 5 s
const ASYNC_POLL_MAX_MS = 180_000; // hard ceiling (unchanged safety bound)

/**
 * Permission requirements per bridge call type.
 */
const PERMISSION_MAP: Record<string, PluginPermission> = {
  get_user: 'user:profile:read',
  get_theme: 'theme:read',
  image_generate: 'entities:image_generate',
  entities_list_image: 'entities:image_generate',
  audio_generate: 'entities:audio_generate',
  // `entity_invoke` deliberately omitted (ADR-012 Phase M4): its permission
  // is path-dependent — integration entities are gated by their
  // config-declared permission (mirrored in
  // `lib/integration-entity-permissions.ts`), everything else by
  // `entities:invoke`. Resolved at request time inside the case block via
  // `entityInvokeRequiredPermission`, matching the server bridges.
  // `client_entity_invoke` deliberately omitted: the required permission
  // is per-entity (resolved from `pluginInvocable.pluginPermission` at
  // request time inside the case block), so the centralized map can't
  // answer statically.
  vault_documents_pick: 'vault:user-documents:read',
  vault_documents_list: 'vault:documents:read',
  vault_documents_read: 'vault:documents:read',
  vault_documents_read_many: 'vault:documents:read',
  vault_documents_download_url: 'vault:documents:read',
  vault_documents_search: 'vault:documents:read',
  vault_documents_get_urls: 'vault:documents:read',
  vault_documents_write: 'vault:documents:write',
  vault_documents_update: 'vault:documents:write',
  vault_documents_delete: 'vault:documents:write',
  vault_documents_attach: 'vault:documents:write',
  vault_documents_detach: 'vault:documents:write',
  vault_documents_upload_init: 'vault:documents:write',
  vault_documents_upload_finalize: 'vault:documents:write',
  vault_documents_upload: 'vault:documents:write',
  arche_assets_list: 'assets:read',
  arche_assets_index: 'assets:read',
  arche_assets_get_url: 'assets:read',
  open_arche: 'navigation:open_arche',
};

export class PluginBridgeHost {
  private options: PluginBridgeOptions;
  /**
   * Live permission set enforced by every capability gate. Initialized from
   * `options.permissions` and updatable via `updatePermissions()` — the
   * builder preview edits fias-plugin.json without rebuilding the preview
   * HTML (the manifest isn't bundled), so the identical-HTML guard skips the
   * iframe reload and this host instance survives the manifest change. A
   * construction-time snapshot then kept enforcing the STALE set ("Permission
   * denied: entities:invoke not granted" after the manifest was fixed — the
   * Gym/text-to-video incidents). Published mode never calls
   * `updatePermissions()`; its JWT capability-token snapshot semantics are by
   * design.
   */
  private permissions: PluginPermission[];
  private rateLimits = new Map<string, RateLimitBucket>();
  private burstBuckets = new Map<string, BurstBucket>();

  private initRetryTimers: ReturnType<typeof setTimeout>[] = [];

  /**
   * Whether the connected plugin's SDK advertised preview-state support in its
   * `ready` message. Gates capture/restore so older SDKs (and non-preview
   * surfaces) are unaffected and pay no latency.
   */
  private pluginSupportsPreviewState = false;
  /** One-shot guard so a repeated `ready` (e.g. StrictMode) doesn't re-restore. */
  private hasRestored = false;
  /** Resolver for an in-flight captureState() awaiting the plugin's reply. */
  private pendingStateCapture: {
    resolve: (snapshot: unknown) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;

  /** Timestamp of the last APPLIED ai_actions_declare (coalescing window base). */
  private lastAiDeclareAt = 0;

  /** Timestamp of the last honoured `ai_open` request (debounce base). */
  private lastAiOpenAt = 0;
  /** Declaration held for the window edge (trailing-edge coalescing; latest wins). */
  private heldAiDeclaration: {
    declaration: PluginRuntimeAiDeclaration;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  /** Timestamp of the last APPLIED ai_state snapshot. */
  private lastAiStateAt = 0;
  /** Snapshot held for the window edge (trailing-edge coalescing; latest wins). */
  private heldAiState: {
    state: Record<string, unknown>;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  /** Monotonic id source for ai_action_dispatch message ids. */
  private aiDispatchSeq = 0;
  /**
   * In-flight ai_action dispatches awaiting the plugin's `ai_action_result`,
   * keyed by messageId. The assistant dispatches one ui_action at a time, but
   * a Map keeps a straggler reply from a timed-out dispatch from being
   * mistaken for the current one.
   */
  private pendingAiDispatches = new Map<
    string,
    {
      resolve: () => void;
      reject: (err: Error) => void;
      timer: ReturnType<typeof setTimeout>;
    }
  >();

  /**
   * In-flight WS-async bridge requests (today: `image_generate`).
   *
   * Each entry holds the Promise resolver pair that completes the
   * outer `handleRequest` Promise, plus a cleanup callback that
   * unsubscribes the WS listeners and clears the safety timeout. Keyed
   * by the server-issued `requestId` so the WS callbacks (which fire
   * for every in-flight subscription the user owns) can filter to the
   * matching entry and ignore broadcasts for other plugins/contexts.
   *
   * Cleanup runs on EVERY terminal path — WS success, WS error,
   * safety-timeout fallback, host `destroy()` — so listeners cannot
   * leak across plugin sessions. See `plan §"Cleanup contract"`.
   */
  private pendingImageRequests = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (err: Error) => void;
      cleanup: () => void;
    }
  >();

  /**
   * In-flight WS-async `surface_invoke` requests (surfaces with
   * `asyncDispatch`). Same contract as `pendingImageRequests` — keyed by
   * the server `requestId`, torn down on every terminal path.
   */
  private pendingSurfaceRequests = new Map<
    string,
    {
      resolve: (value: unknown) => void;
      reject: (err: Error) => void;
      cleanup: () => void;
    }
  >();

  constructor(options: PluginBridgeOptions) {
    this.options = options;
    this.permissions = [...options.permissions];
    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.handleMessage);
  }

  /**
   * Replace the enforced permission set in place (preview mode: the manifest
   * changed but the preview HTML — and therefore this host instance — did
   * not). Re-sends `init` so the plugin-side SDK's view of its permissions
   * refreshes too; the SDK ignores duplicate init after the first, so the
   * refresh is best-effort for the plugin but authoritative for the host's
   * own gates, which read the live field on every request.
   */
  updatePermissions(perms: PluginPermission[]): void {
    this.permissions = [...perms];
    this.sendInit();
  }

  /**
   * Replace the preview store catalog in place (preview mode: the manifest's
   * `products` changed but the preview HTML did not — same surviving-bridge
   * situation as updatePermissions). The store stubs read
   * `options.previewStoreProducts` on every call, so a live replace is all
   * that's needed.
   */
  updatePreviewStoreProducts(
    products: NonNullable<PluginBridgeOptions['previewStoreProducts']>,
  ): void {
    this.options.previewStoreProducts = [...products];
  }

  /**
   * Lazily-built manager for subscribe-kind entity verbs (ADR-012 Phase M4).
   * Lives in its own module — the freeze/coverage arch tests scan this
   * file's `case` labels, and the manager is deliberately entity-agnostic
   * (envelope-shape detection, never entity-ID switches).
   */
  private subscriptionManager: PluginSubscriptionManager | null = null;
  private subscriptionEventCounter = 0;

  private getSubscriptionManager(): PluginSubscriptionManager {
    if (!this.subscriptionManager) {
      this.subscriptionManager = new PluginSubscriptionManager({
        // Host-initiated re-handshakes (renewal/reconnect) bypass the
        // iframe-facing rate limiter on purpose; the server's
        // data_subscribe bucket is authoritative.
        invoke: (payload) => this.callBackendOp('entity_invoke', payload),
        socket: getWebSocket(WEBSOCKET_ENDPOINT),
        getSocketTicket: () => authApi.getWebSocketTicket(),
        sendToPlugin: (message) => {
          const { type, ...payload } = message as { type: string } & Record<string, unknown>;
          this.sendToPlugin({
            type,
            messageId: `sub_${++this.subscriptionEventCounter}`,
            payload,
          });
        },
      });
    }
    return this.subscriptionManager;
  }

  /**
   * Send the init message to the plugin iframe to establish the bridge.
   *
   * When loading via srcDoc, the iframe's onLoad fires before ES module
   * scripts (loaded from esm.sh) have executed. The SDK's message listener
   * may not exist yet, so we retry init a few times with increasing delays.
   * The SDK ignores duplicate init messages after the first.
   */
  init(): void {
    this.sendInit();
    this.initRetryTimers = [
      setTimeout(() => this.sendInit(), 200),
      setTimeout(() => this.sendInit(), 1000),
      setTimeout(() => this.sendInit(), 3000),
    ];
  }

  private sendInit(): void {
    this.sendToPlugin({
      type: 'init',
      messageId: 'init_0',
      payload: {
        archId: this.options.archId,
        permissions: this.permissions,
        theme: this.options.getTheme(),
        // Vendored-library asset URLs — only for plugins that declared the
        // capability, so ordinary plugins never see CDN internals in their init
        // payload.
        vendoredAssets:
          this.permissions.includes('sandbox:vendored-libraries') && this.options.getVendoredAssets
            ? this.options.getVendoredAssets()
            : null,
        currentPath: window.location.pathname,
        // Protocol-version handshake (see HOST_BRIDGE_PROTOCOL_VERSION
        // block above). The SDK rejects hosts older than its required
        // minimum; the host rejects plugins older than ours via the
        // `ready` message handler below.
        hostProtocolVersion: HOST_BRIDGE_PROTOCOL_VERSION,
        minSdkProtocolVersion: HOST_REQUIRES_SDK_PROTOCOL_VERSION,
      },
    });
  }

  /**
   * Notify the plugin of a theme change.
   */
  sendThemeUpdate(): void {
    this.sendToPlugin({
      type: 'theme_update',
      messageId: `theme_${Date.now()}`,
      payload: this.options.getTheme(),
    });
  }

  /**
   * Navigate the plugin to a specific step/node.
   */
  navigateToStep(nodeId: string): void {
    this.sendToPlugin({
      type: 'step_navigate',
      messageId: `step_${Date.now()}`,
      payload: { nodeId },
    });
  }

  /**
   * Ask the running plugin for a serializable snapshot of its transient UI
   * state, so the host can restore it after a preview rebuild reloads the
   * iframe (which otherwise destroys that state — e.g. a generated report).
   *
   * Resolves with the snapshot (whatever the plugin returns) or `null` if the
   * plugin doesn't support the protocol or doesn't reply within
   * `PREVIEW_STATE_CAPTURE_TIMEOUT_MS`. Never rejects — a failed capture must
   * not block the rebuild.
   */
  captureState(): Promise<unknown> {
    if (!this.pluginSupportsPreviewState) return Promise.resolve(null);
    // A previous capture that's still pending is superseded — resolve it null.
    if (this.pendingStateCapture) {
      clearTimeout(this.pendingStateCapture.timer);
      this.pendingStateCapture.resolve(null);
      this.pendingStateCapture = null;
    }
    return new Promise<unknown>((resolve) => {
      const finish = (snapshot: unknown) => {
        if (this.pendingStateCapture) {
          clearTimeout(this.pendingStateCapture.timer);
          this.pendingStateCapture = null;
        }
        resolve(snapshot);
      };
      const timer = setTimeout(() => finish(null), PREVIEW_STATE_CAPTURE_TIMEOUT_MS);
      this.pendingStateCapture = { resolve: finish, timer };
      this.sendToPlugin({
        type: 'preview_state_capture',
        messageId: `pscap_${Date.now()}`,
        payload: {},
      });
    });
  }

  /**
   * Apply a parsed runtime declaration now, or hold it for the coalescing
   * window's edge (latest wins). A held declaration is NEVER dropped — the
   * content-diffed SDK will not re-send it, so a drop here would strand the
   * plugin on stale verbs until its next unrelated change.
   */
  private applyOrHoldAiDeclaration(declaration: PluginRuntimeAiDeclaration): void {
    if (this.heldAiDeclaration) {
      // Newer declaration supersedes the held one; the pending timer stands.
      this.heldAiDeclaration.declaration = declaration;
      return;
    }
    const now = Date.now();
    const remaining = AI_ACTIONS_MIN_REDECLARE_INTERVAL_MS - (now - this.lastAiDeclareAt);
    if (remaining <= 0) {
      this.lastAiDeclareAt = now;
      this.options.onAiActionsDeclare?.(declaration);
      return;
    }
    const holder = {
      declaration,
      timer: setTimeout(() => {
        this.heldAiDeclaration = null;
        this.lastAiDeclareAt = Date.now();
        this.options.onAiActionsDeclare?.(holder.declaration);
      }, remaining),
    };
    this.heldAiDeclaration = holder;
  }

  private cancelHeldAiDeclaration(): void {
    if (this.heldAiDeclaration) {
      clearTimeout(this.heldAiDeclaration.timer);
      this.heldAiDeclaration = null;
    }
  }

  /** ai_state twin of applyOrHoldAiDeclaration — trailing edge, latest wins. */
  private applyOrHoldAiState(state: Record<string, unknown>): void {
    if (this.heldAiState) {
      this.heldAiState.state = state;
      return;
    }
    const now = Date.now();
    const remaining = AI_STATE_MIN_INTERVAL_MS - (now - this.lastAiStateAt);
    if (remaining <= 0) {
      this.lastAiStateAt = now;
      this.options.onAiState?.(state);
      return;
    }
    const holder = {
      state,
      timer: setTimeout(() => {
        this.heldAiState = null;
        this.lastAiStateAt = Date.now();
        this.options.onAiState?.(holder.state);
      }, remaining),
    };
    this.heldAiState = holder;
  }

  /**
   * Dispatch a Fias-AI-requested action into the plugin and await its result.
   * Host→plugin request/response, mirroring captureState()'s shape but keyed
   * by messageId (a straggler reply from a timed-out dispatch must not settle
   * the current one). Resolves when the plugin replies `{ok: true}`; rejects
   * on `{ok: false}`, on timeout, or on bridge teardown — the rejection is
   * what the assistant loop reports to the model as the action's failure.
   */
  dispatchAiAction(actionId: string, params: Record<string, unknown>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const messageId = `aiact_${Date.now()}_${++this.aiDispatchSeq}`;
      const timer = setTimeout(() => {
        this.pendingAiDispatches.delete(messageId);
        reject(
          new Error(`the plugin did not report a result within ${AI_ACTION_DISPATCH_TIMEOUT_MS}ms`),
        );
      }, AI_ACTION_DISPATCH_TIMEOUT_MS);
      this.pendingAiDispatches.set(messageId, { resolve, reject, timer });
      this.sendToPlugin({
        type: 'ai_action_dispatch',
        messageId,
        payload: { actionId, params },
      });
    });
  }

  /**
   * Replay a previously-captured state snapshot to the freshly loaded plugin.
   * Called once the plugin signals readiness (so its SDK and restore handler
   * are live). No-op unless the plugin supports the protocol and an
   * `initialPreviewState` was provided.
   */
  private maybeRestoreState(): void {
    if (this.hasRestored || !this.pluginSupportsPreviewState) return;
    const snapshot = this.options.initialPreviewState;
    if (snapshot === undefined || snapshot === null) return;
    this.hasRestored = true;
    this.sendToPlugin({
      type: 'preview_state_restore',
      messageId: `psrest_${Date.now()}`,
      payload: snapshot,
    });
    this.options.onPreviewStateRestored?.();
  }

  /**
   * Clean up event listeners and release all in-memory state.
   *
   * The per-call Maps (rateLimits, burstBuckets) are bounded by the number
   * of unique message types (~20) so they don't grow unbounded on their
   * own, but preview Maps track plugin-controlled keys. Clearing them here
   * prevents re-mount loops in dev from accumulating state across hosts.
   */
  destroy(): void {
    for (const timer of this.initRetryTimers) clearTimeout(timer);
    this.initRetryTimers = [];
    // Resolve any in-flight state capture so an awaiting onBeforePreviewReload
    // doesn't hang on a torn-down bridge.
    if (this.pendingStateCapture) {
      clearTimeout(this.pendingStateCapture.timer);
      this.pendingStateCapture.resolve(null);
      this.pendingStateCapture = null;
    }
    // Reject in-flight AI action dispatches so the assistant loop's awaiting
    // applyAction settles deterministically instead of riding out its timeout.
    for (const [, entry] of this.pendingAiDispatches) {
      clearTimeout(entry.timer);
      entry.reject(new Error('the plugin was closed before the action completed'));
    }
    this.pendingAiDispatches.clear();
    // Drop coalescing holders — their callbacks must not fire post-teardown.
    this.cancelHeldAiDeclaration();
    if (this.heldAiState) {
      clearTimeout(this.heldAiState.timer);
      this.heldAiState = null;
    }
    // Tear down any in-flight WS-async bridge requests so the iframe's
    // awaiting Promises reject deterministically. The cleanup function
    // unsubs the WS listeners + clears the safety timer; the reject
    // surfaces to the outer `handleRequest` await which posts an error
    // response into the iframe.
    for (const [, entry] of this.pendingImageRequests) {
      entry.cleanup();
      entry.reject(new Error('plugin_unmounted'));
    }
    this.pendingImageRequests.clear();
    for (const [, entry] of this.pendingSurfaceRequests) {
      entry.cleanup();
      entry.reject(new Error('plugin_unmounted'));
    }
    this.pendingSurfaceRequests.clear();
    // Tear down realtime subscriptions: clears renewal/ack/debounce timers,
    // unsubscribes the singleton socket's topics + listeners. Same
    // cleanup-on-every-terminal-path contract as the pending maps above.
    this.subscriptionManager?.destroy();
    this.subscriptionManager = null;
    window.removeEventListener('message', this.handleMessage);
    this.rateLimits.clear();
    this.burstBuckets.clear();
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    // Only handle messages from our plugin iframe
    if (event.source !== this.options.iframe.contentWindow) return;

    const data = event.data;
    if (!data || typeof data !== 'object' || !data.type) return;

    // Plugin SDK requesting init (handles srcDoc module loading race condition)
    if (data.type === 'request_init') {
      this.sendInit();
      return;
    }

    // All other messages require messageId
    if (!(data as BridgeMessage).messageId) return;

    // Fire-and-forget messages (no response needed)
    if (data.type === 'ready') {
      // Plugin acknowledged init — stop retrying
      for (const timer of this.initRetryTimers) clearTimeout(timer);
      this.initRetryTimers = [];

      // Protocol-version compatibility check. The SDK ≥ v1 sends its
      // `sdkProtocolVersion` in the ready payload. An older SDK omits
      // the field — that's treated as v1 (the first published contract),
      // which is forward-compatible if our minimum is also v1. Once we
      // bump HOST_REQUIRES_SDK_PROTOCOL_VERSION past 1, a missing field
      // becomes "too old" automatically.
      const payload = (data as BridgeMessage).payload as
        | { sdkProtocolVersion?: number; supportsPreviewState?: boolean }
        | undefined;
      const sdkVersion = payload?.sdkProtocolVersion ?? 1;
      if (sdkVersion < HOST_REQUIRES_SDK_PROTOCOL_VERSION) {
        this.options.onLog?.(
          'warn',
          `Plugin bridge SDK v${sdkVersion} is older than the minimum this host accepts (v${HOST_REQUIRES_SDK_PROTOCOL_VERSION}).`,
        );
        this.options.onIncompatibleSdk?.(sdkVersion, HOST_REQUIRES_SDK_PROTOCOL_VERSION);
      }
      // Preview state preservation is gated on the plugin advertising support,
      // and only meaningful in preview mode. Now that the plugin's SDK is live,
      // replay any snapshot captured before this reload.
      this.pluginSupportsPreviewState =
        this.options.previewMode === true && payload?.supportsPreviewState === true;
      this.maybeRestoreState();
      return;
    }

    // Plugin's reply to a captureState() request — its serializable snapshot.
    if (data.type === 'preview_state') {
      let snapshot = (data as BridgeMessage).payload ?? null;
      // Defensive cap: drop an oversized snapshot rather than ferry it across
      // the reload. The report just won't restore — never an OOM.
      if (snapshot !== null) {
        let bytes = 0;
        try {
          bytes = JSON.stringify(snapshot).length;
        } catch {
          // Non-serializable (shouldn't happen post-structured-clone) — drop it.
          bytes = PREVIEW_STATE_MAX_BYTES + 1;
        }
        if (bytes > PREVIEW_STATE_MAX_BYTES) {
          this.options.onLog?.(
            'warn',
            `Preview state snapshot dropped: ${bytes} bytes exceeds the ${PREVIEW_STATE_MAX_BYTES}-byte limit.`,
          );
          snapshot = null;
        }
      }
      this.pendingStateCapture?.resolve(snapshot);
      return;
    }

    if (data.type === 'resize') {
      const { height } = data.payload as { height: number };
      if (typeof height === 'number' && height > 0) {
        this.options.onResize?.(height);
      }
      return;
    }

    if (data.type === 'toast') {
      const { message, variant } = data.payload as { message: string; variant: string };
      if (typeof message === 'string') {
        this.options.onToast?.(message, variant ?? 'info');
      }
      return;
    }

    if (data.type === 'subscription_unsubscribe') {
      // Fire-and-forget release of one logical realtime subscription
      // (ADR-012 Phase M4). Refcounted: the socket topic is torn down only
      // when the last logical subscriber releases. In the if-chain (not a
      // case label) — the freeze/coverage arch tests scan case labels.
      const { subscriptionId } = data.payload as { subscriptionId?: string };
      if (typeof subscriptionId === 'string') {
        this.subscriptionManager?.release(subscriptionId);
      }
      return;
    }

    // Runtime Fias AI action declaration — fire-and-forget metadata
    // registration (client-plane, same family as resize/toast: performs
    // nothing, bills nothing, touches no server). In the if-chain, NOT a
    // case label — the freeze/coverage arch tests scan case labels, and this
    // is transport under the ui_action exception (docs/capability-placement.md).
    // The lane is unmetered (checkRateLimit runs after this chain), so every
    // bound is enforced right here. INVALID declarations are dropped with a
    // warn; VALID ones arriving too fast are trailing-edge coalesced (latest
    // wins, applied at the window edge) — a legitimate declaration must never
    // be silently lost, because the content-diffed SDK will not re-send it.
    if (data.type === 'ai_actions_declare') {
      if (!this.permissions.includes('ai:actions')) {
        this.options.onLog?.(
          'warn',
          'ai_actions_declare ignored: "ai:actions" permission not granted',
        );
        return;
      }
      const payload = (data as BridgeMessage).payload;
      if (payload === null) {
        // Explicit clear of a previous declaration — always allowed and
        // immediate: cancel anything held, reset the throttle so a
        // clear→declare pair (React StrictMode's unmount/remount of
        // useFiasAIActions) applies without waiting out a window.
        this.cancelHeldAiDeclaration();
        this.lastAiDeclareAt = 0;
        this.options.onAiActionsDeclare?.(null);
        return;
      }
      const parsed = parseRuntimeAiDeclaration(payload);
      if ('error' in parsed) {
        this.options.onLog?.('warn', `ai_actions_declare dropped: ${parsed.error}`);
        return;
      }
      this.applyOrHoldAiDeclaration(parsed.declaration);
      return;
    }

    // Plugin state snapshot for the assistant. Size-capped HARD (the server
    // does not bound archeContext.state and stringifies it into the system
    // prompt every turn — oversized snapshots are dropped with a warn) and
    // frequency-coalesced: each accepted snapshot re-renders the host
    // (setState → merge/filter recompute), so a plugin pushing per-frame
    // must not drive the host at frame rate. Trailing edge — the LATEST
    // snapshot always lands.
    if (data.type === 'ai_state') {
      if (!this.permissions.includes('ai:actions')) {
        this.options.onLog?.('warn', 'ai_state ignored: "ai:actions" permission not granted');
        return;
      }
      const payload = (data as BridgeMessage).payload;
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        this.options.onLog?.('warn', 'ai_state dropped: payload must be an object');
        return;
      }
      let bytes = 0;
      try {
        bytes = JSON.stringify(payload).length;
      } catch {
        this.options.onLog?.('warn', 'ai_state dropped: payload not serializable');
        return;
      }
      if (bytes > AI_STATE_MAX_BYTES) {
        this.options.onLog?.(
          'warn',
          `ai_state dropped: ${bytes} bytes exceeds the ${AI_STATE_MAX_BYTES}-byte cap (the snapshot enters every turn's system prompt)`,
        );
        return;
      }
      // Same banned-character scan as action text: trusted snapshots reach
      // the system prompt verbatim, and control/bidi/zero-width characters
      // have no legitimate use in a state flag map at any trust level.
      // Deep walk, not a serialized scan — JSON.stringify escapes C0 bytes.
      if (containsBannedChars(payload)) {
        this.options.onLog?.('warn', 'ai_state dropped: contains control characters');
        return;
      }
      this.applyOrHoldAiState(payload as Record<string, unknown>);
      return;
    }

    // Plugin requests the HOST open the global Fias AI panel — the
    // discoverability lane's only plugin-initiated affordance. A BARE signal
    // by design: no payload is read, ever — an "open with this prefilled
    // message" variant would be a prompt-injection channel from an untrusted
    // iframe into the assistant's input. Same host-UI-mediation transport
    // exception as the rest of the ai_* lane (docs/capability-placement.md).
    if (data.type === 'ai_open') {
      if (!this.permissions.includes('ai:actions')) {
        this.options.onLog?.('warn', 'ai_open ignored: "ai:actions" permission not granted');
        return;
      }
      const now = Date.now();
      if (now - this.lastAiOpenAt < AI_OPEN_MIN_INTERVAL_MS) {
        this.options.onLog?.(
          'warn',
          `ai_open ignored: debounced (at most one open request per ${AI_OPEN_MIN_INTERVAL_MS / 1000}s)`,
        );
        return;
      }
      this.lastAiOpenAt = now;
      this.options.onAiOpenRequest?.();
      return;
    }

    // Plugin's reply to an ai_action_dispatch — completes the in-flight
    // Promise the renderer's applyAction is awaiting.
    if (data.type === 'ai_action_result') {
      // Uniform lane gate. Redundant today (dispatches only exist for
      // declared actions, which require the permission) but keeps the whole
      // ai_* lane permission-gated without relying on that reasoning.
      if (!this.permissions.includes('ai:actions')) {
        this.options.onLog?.(
          'warn',
          'ai_action_result ignored: "ai:actions" permission not granted',
        );
        return;
      }
      const pending = this.pendingAiDispatches.get((data as BridgeMessage).messageId);
      if (!pending) return; // straggler reply after timeout — already settled
      clearTimeout(pending.timer);
      this.pendingAiDispatches.delete((data as BridgeMessage).messageId);
      const payload = (data as BridgeMessage).payload as
        | { ok?: boolean; error?: string }
        | undefined;
      if (payload?.ok === true) {
        pending.resolve();
      } else {
        pending.reject(
          new Error(
            typeof payload?.error === 'string' &&
              payload.error.length > 0 &&
              payload.error.length <= 1000
              ? payload.error
              : 'the plugin reported the action failed',
          ),
        );
      }
      return;
    }

    if (data.type === 'navigate') {
      const { path } = data.payload as { path: string };
      if (typeof path !== 'string' || path.length === 0) {
        const message = `Bridge navigate rejected: path must be a non-empty string (got: ${typeof path === 'string' ? '<empty>' : '<missing>'})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      // Plugins navigate in their own URL space (e.g. `/edit/<id>` or
      // `/shared/<id>`); the platform's URL space is `/a/<archeId>/...`.
      // Auto-prepend the plugin's own arche prefix when the plugin sends
      // a root-relative path so plugin code doesn't have to know its own
      // archeId or the platform path convention. Mirrors how `currentPath`
      // is delivered inbound — both sides agree on the platform path.
      //
      // Security: we always scope to *this plugin's own* archeId. A path
      // already in `/a/<own-archeId>/...` form is passed through; one
      // pointing at a different arche is rejected so a plugin can't
      // navigate the parent window into someone else's arche space.
      const ownPrefix = `/a/${this.options.archId}`;
      let resolved: string | null = null;
      if (path === ownPrefix || path.startsWith(`${ownPrefix}/`)) {
        resolved = path;
      } else if (path.startsWith('/a/')) {
        const message = `Bridge navigate rejected: cross-arche navigation not allowed (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      } else if (path.startsWith('/')) {
        resolved = path === '/' ? ownPrefix : `${ownPrefix}${path}`;
      }
      if (!resolved) {
        const message = `Bridge navigate rejected: path must be root-relative (got: ${path})`;
        this.options.onLog?.('error', message);
        this.sendError(data.messageId, message);
        return;
      }
      this.options.onNavigate?.(resolved);
      // Acknowledge so the plugin's `await bridge.request('navigate', ...)`
      // resolves instead of timing out 30s later.
      this.sendResponse(data.messageId, null);
      return;
    }

    // Request/response messages — check permissions and rate limits
    try {
      const result = await this.handleRequest(data);
      this.sendResponse(data.messageId, result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      // Surface the error to the preview log stream so get_preview_logs picks
      // it up. Without this the plugin gets a rejected Promise but the AI
      // has no visibility unless the plugin code explicitly console.errors.
      // BridgeBurstError is the exception: the burst path manages its own
      // onLog (fires once on breach, silent during cooldown) — re-logging
      // here would double-log the breach and drown the logs during cooldown.
      if (!(err instanceof BridgeBurstError)) {
        this.options.onLog?.('error', `Bridge request failed (${data.type}): ${message}`);
      }
      // Forward the backend's machine-readable code (when the failure came
      // from a backend fetch) so the SDK-side FiasBridgeError carries it.
      this.sendError(
        data.messageId,
        message,
        err instanceof HostBridgeError ? err.code : undefined,
      );
    }
  }

  private async handleRequest(msg: BridgeMessage): Promise<unknown> {
    // Permission checks run in ALL modes, including preview. The builder/dev
    // preview must fail exactly as a deployed arche would when the manifest
    // omits a permission the code uses — otherwise a missing-permission bug
    // (e.g. calling useFiasUser() without `user:profile:read`) stays hidden
    // until production, where it surfaces as an infinite "loading" hang. The
    // in-memory preview backends below (storage/data/etc.) are unaffected;
    // only the capability gate is now enforced identically to production.
    const requiredPermission = PERMISSION_MAP[msg.type];
    if (requiredPermission && !this.permissions.includes(requiredPermission)) {
      throw new Error(`Permission denied: ${requiredPermission} not granted`);
    }

    // Rate limiting applies in all modes (protects against runaway loops in preview)
    this.checkRateLimit(msg.type, msg.payload);

    switch (msg.type) {
      case 'get_user':
        return this.options.getUser();

      case 'get_theme':
        return this.options.getTheme();

      case 'open_arche': {
        // Cross-arche navigation. The permission check above
        // (`navigation:open_arche`) and the format validation here are the
        // whole trust boundary — the host resolves the id to `/a/<archeId>`
        // and navigates itself, so the iframe sandbox never needs popups or
        // top-navigation. We never accept a free-form path: only a
        // well-formed arche id, so a plugin cannot steer the host to an
        // arbitrary route or origin.
        const payload =
          msg.payload && typeof msg.payload === 'object'
            ? (msg.payload as { archeId?: unknown; newTab?: unknown })
            : null;
        const archeId = typeof payload?.archeId === 'string' ? payload.archeId : '';
        if (!isValidArcheIdFormat(archeId)) {
          throw new Error(
            `open_arche rejected: archeId must be a valid arche id (got: ${archeId ? archeId : '<missing>'})`,
          );
        }
        this.options.onOpenArche?.(archeId, payload?.newTab === true);
        return null;
      }

      case 'entity_invoke': {
        const payload = msg.payload as {
          // String id, or a selector object ({ capability } for text) — both
          // are valid SDK forms; the resolvers downstream handle each.
          entityId: string | Record<string, unknown>;
          input: string;
          stream?: boolean;
          systemPrompt?: string;
          images?: Array<{ mediaType: string; dataBase64: string }>;
        };
        // Path-dependent permission gate (ADR-012 Phase M4) — the op has no
        // PERMISSION_MAP entry; the required permission depends on the
        // target entity. Runs in ALL modes (preview parity: a missing
        // manifest permission must fail here exactly as in production).
        const requiredEntityInvokePermission = entityInvokeRequiredPermission(payload.entityId);
        if (!this.permissions.includes(requiredEntityInvokePermission)) {
          throw new Error(`Permission denied: ${requiredEntityInvokePermission} not granted`);
        }
        // Store (IAP) via entity_invoke (ADR-012 Phase L, Job 3): route to the
        // shared store handler so the host-mediated purchase-confirmation +
        // preview simulation + the store family's legacy wire SHAPES are
        // preserved on the entity_invoke path exactly as on the `store_*`
        // aliases. Store is NOT a synthetic-tenant preview entity, so preview
        // MUST short-circuit client-side (inside handleStoreOp) and never proxy.
        if (payload.entityId === PLUGIN_STORE_ENTITY_ID) {
          const inner = ((msg.payload as { payload?: Record<string, unknown> }).payload ??
            {}) as Record<string, unknown>;
          const { op: rawOp, ...storeArgs } = inner;
          const storeOp = typeof rawOp === 'string' ? rawOp : '';
          return this.handleStoreOp(storeOp, storeArgs, async (o, a) => {
            const body = await this.proxyToBackend({
              ...msg,
              payload: { entityId: PLUGIN_STORE_ENTITY_ID, payload: { op: o, ...a } },
            });
            return (body as { output?: unknown }).output;
          });
        }
        // Mirrored integration entities (the free/state families) go to the
        // live bridge EVEN IN PREVIEW — M3 serves preview tenants on the
        // production pipeline; only AI/text invocation stays on the preview
        // session hooks below.
        const isMirroredIntegration =
          typeof payload.entityId === 'string' &&
          INTEGRATION_ENTITY_PERMISSIONS[payload.entityId] !== undefined;
        if (this.options.previewMode && this.options.onEntityInvoke && !isMirroredIntegration) {
          if (payload.stream) {
            return this.handlePreviewStreamingInvoke(msg.messageId, payload);
          }
          return this.options.onEntityInvoke(payload);
        }
        // Published plugins: handle streaming via SSE through the bridge endpoint
        if (payload.stream) {
          return this.handlePublishedStreamingInvoke(msg.messageId, payload);
        }
        const body = await this.proxyToBackend(msg);
        // Subscribe-kind verb responses carry a subscription envelope — the
        // manager consumes grant/topic and hands the iframe a host-minted
        // logical id. Everything else passes through untouched.
        return this.getSubscriptionManager().handleInvokeResponse(
          body,
          msg.payload as Record<string, unknown>,
        );
      }

      case 'image_generate': {
        const imgPayload = msg.payload as {
          entityId: string | Record<string, unknown>;
          prompt: string;
          size?: string;
          quality?: string;
          style?: string;
        };
        if (this.options.previewMode && this.options.onImageGenerate) {
          return this.options.onImageGenerate(imgPayload);
        }
        // Async-via-WebSocket path: POST returns `{ requestId, status:
        // 'pending' }` immediately; the final `ImageInvokeResult`
        // arrives over WS as an `image_complete` broadcast. Pre-refactor
        // this case awaited the full 30–90 s provider call on a single
        // HTTP request and hit the internal ALB's 60 s idle timeout. See
        // image-generate-bridge-handler.ts file header.
        return this.handleAsyncImageGenerate(msg);
      }

      case 'entities_list_image': {
        // Discovery op backing the SDK's `useImageEntities` hook. Pure
        // registry read on the server — no preview-mode hook needed
        // (preview can call the live registry and stays in sync with
        // production).
        return this.proxyToBackend(msg);
      }

      case 'client_entity_invoke': {
        // Generic dispatcher for `executionMode: 'client-side'` entities.
        // Resolves the entity registration by ID (from the client-side
        // registry — populated by executor module imports at startup),
        // gates on the entity's declared `pluginPermission`, and hands off
        // to the registry. No per-entity case needed: adding a new
        // client-side entity is an entity-config + executor change, never
        // a bridge change.
        const payload =
          msg.payload && typeof msg.payload === 'object'
            ? (msg.payload as { entityId?: unknown; input?: unknown })
            : null;
        if (!payload || typeof payload.entityId !== 'string') {
          throw new Error('client_entity_invoke: payload.entityId (string) is required');
        }
        const registration = getClientEntityRegistration(payload.entityId);
        if (!registration) {
          throw new Error(
            `client_entity_invoke: no client-side executor registered for ${payload.entityId}`,
          );
        }
        // Per-entity permission (resolved from the entity's registration).
        // Enforced in all modes, including preview — parity with production
        // and with the centralized check in handleRequest above.
        const requiredPermission = registration.pluginPermission as PluginPermission;
        if (!this.permissions.includes(requiredPermission)) {
          throw new Error(`Permission denied: ${requiredPermission} not granted`);
        }
        const user = await this.options.getUser();
        return invokeClientEntity(payload.entityId, payload.input, {
          userId: user.userId,
          archeId: this.options.archId,
        });
      }

      case 'audio_generate': {
        // Audio generation runs through the server-side `audio_generate`
        // bridge op. In builder preview the server answers 403
        // PREVIEW_UNSUPPORTED_OP (billed path with no builder-session cost
        // accounting — see preview-bridge-gate.ts); the rejection surfaces
        // in the preview logs with that message.
        return this.proxyToBackend(msg);
      }

      // Storage / Data Store / Workspaces / Store-IAP: the legacy op-family
      // alias ops were retired in ADR-012 Phase L Job 3 Step 2. New bundles
      // reach these capabilities via `entity_invoke` (handled in the case
      // above — the mirrored-integration branch proxies to the live bridge in
      // preview and production alike; store routes through the shared
      // `handleStoreOp`). An old, immutable published bundle that still emits a
      // bare alias falls through to `default`, which raises a clear "plugin
      // needs an update from its developer" notice rather than a cryptic error.

      // ── Vault Documents (Phase 12 substrate, scoped to source_arche_id) ──
      // The server-side bridge handler enforces source_arche_id == this arche
      // (or attachment via vault_document_references) and proxies through
      // vault-documents-operations.ts. Preview mode has no Vault store, so
      // these return empty results / synthetic values to keep render paths
      // from crashing.
      // ── Vault user-document picker (host-UI mediation) ──────────────
      // The purchase-sheet pattern applied to user data: the HOST renders
      // the picker + consent sheet; the plugin never sees the user's vault
      // list, only what was hand-picked and confirmed (metadata — content
      // flows through the audited consented-read entity verbs). Client-
      // host-only op (no server case), like `open_arche`; sanctioned as a
      // transport exception in docs/capability-placement.md + ADR-012.
      case 'vault_documents_pick': {
        if (this.options.previewMode || !this.options.onVaultPickRequest) {
          // No user data in the preview, ever — and a host without the
          // callback (embeds, tests) fails safe as a cancel.
          return { canceled: true };
        }
        const req = msg.payload as { maxDocuments?: unknown } | null;
        const maxDocuments = Math.max(
          1,
          Math.min(typeof req?.maxDocuments === 'number' ? req.maxDocuments : 25, 25),
        );
        return this.options.onVaultPickRequest({ maxDocuments });
      }

      // Vault ops in preview FAIL FAST, mirroring the server's
      // PREVIEW_UNSUPPORTED_OP. These used to be success-shaped stubs
      // (write returned a synthetic documentId, update/delete returned
      // success, list/read returned empty) — so in the builder preview a
      // "successful" write silently vanished, the exact
      // preview-lies-about-production failure class the capability work
      // exists to kill (first surfaced by the kitchen-sink verification
      // arche). The documented behavior — catalog previewNotes, the
      // derived preview section, SDK CLAUDE.md — has always been "vault
      // ops are not available in preview"; now the code says the same.
      case 'vault_documents_list':
      case 'vault_documents_read':
      case 'vault_documents_read_many':
      case 'vault_documents_download_url':
      case 'vault_documents_get_urls':
      case 'vault_documents_write':
      case 'vault_documents_update':
      case 'vault_documents_delete':
      case 'vault_documents_search':
      case 'vault_documents_attach':
      case 'vault_documents_detach': {
        if (this.options.previewMode) {
          throw new Error(
            `${msg.type} is not available in the builder preview — vault documents first run for real after publish (or in the dev harness's Live mode).`,
          );
        }
        return this.proxyToBackend(msg);
      }
      case 'vault_documents_upload_init':
      case 'vault_documents_upload_finalize': {
        if (this.options.previewMode) {
          // The two-step upload protocol (init → PUT bytes to
          // presigned URL → finalize) needs a real PUT endpoint that
          // preview mode cannot provide. Returning a synthetic
          // uploadUrl like 'about:blank' would just produce a
          // confusing fetch failure when the SDK tries to upload.
          // Fail fast so plugin code can show a clearer message.
          throw new Error(
            `${msg.type} is not supported in preview mode (no presigned-URL endpoint). Test binary uploads in the published or dev-harness-live runtime.`,
          );
        }
        return this.proxyToBackend(msg);
      }

      case 'vault_documents_upload': {
        // Composite binary upload, host-orchestrated. The plugin iframe's
        // CSP is `connect-src 'none'`, so it CANNOT do the presigned PUT
        // itself (the SDK's old in-iframe `fetch` PUT was silently blocked
        // in published plugins). Here the HOST (parent window, no
        // connect-src restriction) runs init → PUT → finalize. The presigned
        // URL comes from the BACKEND (init), never from the iframe, so the
        // iframe cannot point the host at an arbitrary URL (no SSRF).
        if (this.options.previewMode) {
          throw new Error(
            'vault_documents_upload is not supported in preview mode (no presigned-URL endpoint). Test binary uploads in the published or dev-harness-live runtime.',
          );
        }
        return this.handleVaultUpload(msg.payload);
      }

      case 'surface_invoke': {
        // Invocation Surface Pattern dispatch. Per-surface permission,
        // rate-limit, and validation gates all read from the surface
        // metadata server-side, so there's no entry in PERMISSION_MAP
        // here (matches the production backend's gating model).
        //
        // Preview mode proxies to the live bridge like every other op: the
        // server admits surfaces that declare `previewPolicy` and answers 403
        // PREVIEW_UNSUPPORTED_OP for the rest, so the eligibility decision
        // stays in one place instead of being duplicated as a client throw
        // that could drift from it. Preview calls run for real and are billed
        // to the author.
        // Async-capable: surfaces with `asyncDispatch` return `{ requestId,
        // status: 'pending' }` and complete over WebSocket (see
        // surface-async.ts). Synchronous surfaces return their result on the
        // HTTP response and this method passes it straight through.
        return this.handleAsyncSurfaceInvoke(msg);
      }

      // ── Arche Asset Library (contributor-curated published manifest) ──
      // The bridge backend resolves the manifest from the URL path's
      // archeId (server-trusted) and signs short-lived URLs against
      // the published entries. Preview mode has no manifest, so we
      // return an empty list / a synthetic URL.
      case 'arche_assets_list': {
        if (this.options.previewMode) {
          return { entries: [], nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }
      case 'arche_assets_index': {
        if (this.options.previewMode) {
          return { groups: [] };
        }
        return this.proxyToBackend(msg);
      }
      case 'arche_assets_get_url': {
        if (this.options.previewMode) {
          return {
            assetId: 'preview',
            signedUrl: 'about:blank',
            expiresAt: Math.floor(Date.now() / 1000) + 60,
            contentType: 'image/png',
            width: 0,
            height: 0,
            sizeBytes: 0,
            name: null,
            altText: null,
            tags: [],
          };
        }
        return this.proxyToBackend(msg);
      }

      default:
        // ADR-012 Phase L Job 3 Step 2: the legacy op-family aliases
        // (storage_*/data_*/workspace_*/store_*) were retired. An old, immutable
        // published bundle still emits them; surface a clear "plugin needs an
        // update from its developer" notice instead of a cryptic unknown-op error.
        if (RETIRED_ALIAS_OP_RE.test(msg.type)) {
          this.options.onRetiredCapability?.(msg.type);
          throw new Error(
            `This plugin uses a retired capability (${msg.type}) and needs an ` +
              `update from its developer.`,
          );
        }
        throw new Error(`Unknown bridge call type: ${msg.type}`);
    }
  }

  /**
   * Handle streaming entity invoke in preview mode via SSE.
   * Sends stream_token messages to the plugin as tokens arrive,
   * then returns the final result for the normal response message.
   */
  private async handlePreviewStreamingInvoke(
    messageId: string,
    payload: {
      // String config id, or a selector object ({ capability } et al) — the
      // ai-invoke route resolves both (published-bridge parity; the preview
      // used to 400 every documented selector form — kitchen-sink finding).
      entityId: string | Record<string, unknown>;
      input: string;
      systemPrompt?: string;
      images?: Array<{ mediaType: string; dataBase64: string }>;
    },
  ): Promise<unknown> {
    // Preview AI invoke authenticates as the user's SESSION (cookie +
    // CSRF), exactly like the non-streaming preview path
    // (api.invokePluginAI). The `/ai-invoke` route is session-auth
    // (requireAuth); a plugin-bridge capability token is the WRONG
    // credential here. Sending one — or the empty string the builder's
    // own arche (`arche_plugin_builder`, renderMode 'default') yields
    // when its bridge-token mint 400s — produced the "JWT … 3 parts"
    // 401 that surfaced to users as "AI not working". Regressed when the
    // bridge moved to capability tokens (commit 0a08f3c3); `getAuthToken`
    // stays correct for the published bridge path, just not here.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    attachCsrfHeader('POST', headers);
    const response = await fetch(
      `${this.options.bridgeApiUrl}/arches/plugin-builder/sessions/${this.options.archId.replace('preview_', '')}/ai-invoke`,
      {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          input: payload.input,
          entityId: payload.entityId,
          stream: true,
          // Invoke-time systemPrompt — the external-SDK norm; the route gives
          // it precedence over a builder config's stored prompt, matching the
          // published bridge. Previously dropped here, so selector-form
          // invokes silently lost their instructions.
          ...(payload.systemPrompt ? { systemPrompt: payload.systemPrompt } : {}),
          // Vision inputs ride along — the ai-invoke route validates them with
          // the same bounds as the published bridge. Omitted when absent so
          // text-only calls keep their exact prior body shape.
          ...(payload.images && payload.images.length > 0 ? { images: payload.images } : {}),
        }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new HostBridgeError(
        err.error?.message ?? 'AI invocation failed',
        err.error?.code ?? 'BRIDGE_ERROR',
      );
    }

    // If SSE streaming, consume the stream
    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          let parsed: {
            text?: string;
            done?: boolean;
            output?: string;
            error?: unknown;
          } | null = null;
          try {
            parsed = JSON.parse(data);
          } catch {
            // Skip malformed SSE data
            continue;
          }
          // A mid-stream failure frame. Without this branch the reader fell
          // through, resolved with whatever partial text had accumulated, and
          // the plugin saw a SUCCESSFUL call returning a truncated (usually
          // empty) string — a failure mode strictly worse than a clear error.
          if (parsed?.error) {
            // Release the body before unwinding. `retryable: true` encourages
            // plugins to loop on exactly these failures, so a reader left
            // locked until GC accumulates open connections.
            await reader.cancel().catch(() => {});
            throw hostBridgeErrorFromSseFrame(parsed.error);
          }
          if (parsed?.text) {
            fullOutput += parsed.text;
            // Forward token to plugin iframe
            this.options.iframe.contentWindow?.postMessage(
              { type: 'stream_token', messageId, text: parsed.text },
              '*',
            );
          }
          if (parsed?.done && parsed.output) {
            fullOutput = parsed.output;
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-streaming fallback
    return response.json();
  }

  /**
   * Handle streaming entity invoke for published plugins via the bridge endpoint.
   */
  private async handlePublishedStreamingInvoke(
    messageId: string,
    payload: {
      // String id or selector object — the published bridge already resolves
      // both; JSON-serialized verbatim into the request body.
      entityId: string | Record<string, unknown>;
      input: string;
      systemPrompt?: string;
      images?: Array<{ mediaType: string; dataBase64: string }>;
    },
  ): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(this.bridgeEndpoint('entity_invoke', payload), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        type: 'entity_invoke',
        payload: { ...payload, stream: true },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new HostBridgeError(
        err.error?.message ?? 'AI invocation failed',
        err.error?.code ?? 'BRIDGE_ERROR',
      );
    }

    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              await reader.cancel().catch(() => {});
              throw hostBridgeErrorFromSseFrame(parsed.error);
            }
            if (parsed.text) {
              fullOutput += parsed.text;
              this.options.iframe.contentWindow?.postMessage(
                { type: 'stream_token', messageId, text: parsed.text },
                '*',
              );
            }
            if (parsed.done && parsed.output) {
              fullOutput = parsed.output;
            }
          } catch (e) {
            // A HostBridgeError is a deliberate failure signal from the frame
            // above, never a JSON.parse casualty — rethrow it unconditionally.
            // Matching on the message text instead used to swallow exactly the
            // frames this branch exists to surface: `hostBridgeErrorFromSseFrame`
            // falls back to the message 'AI invocation failed' when a structured
            // frame carries a code but no message, which is the same sentinel
            // this filter was comparing against.
            if (e instanceof HostBridgeError) throw e;
            if (e instanceof Error && e.message !== 'AI invocation failed') throw e;
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-SSE fallback
    return response.json();
  }

  /**
   * Escalating poll fallback shared by the image and surface async paths.
   * Stays quiet for `ASYNC_POLL_GRACE_MS` (the WS path normally wins first),
   * then polls every `ASYNC_POLL_INTERVAL_MS` until a terminal result or the
   * `ASYNC_POLL_MAX_MS` ceiling. Returns a canceller the caller wires into its
   * `cleanup()` so a WS-delivered result (or host teardown) stops polling.
   *
   * `poll` is the per-surface single-shot status fetch (already auth-scoped on
   * the server). `onSettled` is invoked at most once with the outcome; the
   * loop guarantees no callback after the canceller runs.
   */
  private startPollFallback(
    poll: () => Promise<
      | { kind: 'pending' }
      | { kind: 'completed'; value: unknown }
      | { kind: 'failed'; code: string; message: string }
      | { kind: 'not_found' }
    >,
    onSettled: (
      outcome:
        | { type: 'completed'; value: unknown }
        | { type: 'failed'; error: Error }
        | { type: 'timeout' },
    ) => void,
  ): () => void {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + ASYNC_POLL_MAX_MS;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      let result: Awaited<ReturnType<typeof poll>>;
      try {
        result = await poll();
      } catch {
        result = { kind: 'pending' };
      }
      if (cancelled) return;

      if (result.kind === 'completed') {
        onSettled({ type: 'completed', value: result.value });
        return;
      }
      if (result.kind === 'failed') {
        onSettled({ type: 'failed', error: new Error(`${result.code}: ${result.message}`) });
        return;
      }
      // `not_found` means the server has no record of this requestId on any
      // replica (the durable request_state row makes this authoritative) —
      // stop rather than poll out the ceiling.
      if (result.kind === 'not_found' || Date.now() >= deadline) {
        onSettled({ type: 'timeout' });
        return;
      }
      timer = setTimeout(() => void tick(), ASYNC_POLL_INTERVAL_MS);
    };

    timer = setTimeout(() => void tick(), ASYNC_POLL_GRACE_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }

  /**
   * Async-via-WebSocket dispatch for `image_generate`.
   *
   * Steps:
   *   1. POST the kickoff. The server returns `{ requestId, status:
   *      'pending' }` in ~10 ms.
   *   2. Subscribe to the shared `FiasWebSocket` for that requestId.
   *      `getWebSocket` lazily fetches its own Cognito-gated ticket
   *      via `authApi.getWebSocketTicket()`, so no extra auth wiring.
   *   3. Register `onImageComplete` + `onError` callbacks filtered by
   *      requestId. Broadcasts for any other in-flight request the
   *      user owns no-op.
   *   4. On match: resolve with the final `ImageInvokeResult` payload
   *      shape (strip broadcast-only `type` and `requestId` fields).
   *      The outer `handleMessage` in turn posts this into the iframe
   *      so the SDK's `await fias.images.generate(...)` resolves
   *      byte-for-byte the same as the pre-refactor synchronous path.
   *   5. Poll fallback: an escalating `image-status` poll (quiet for the
   *      first ~15 s, then every ~5 s up to a 180 s ceiling) recovers a
   *      missed broadcast in seconds. `completed` → resolve; `failed` →
   *      reject; exhausted / `not_found` → reject with
   *      `image_generate_timeout`.
   *   6. Cleanup runs on EVERY terminal path (success / error /
   *      timeout / host destroy) — see `pendingImageRequests`
   *      comment + the cleanup() function below.
   *
   * If the server responds with anything other than `status: 'pending'`
   * (legacy code paths, future inline responses), this method falls
   * through to returning the body as-is.
   */
  private async handleAsyncImageGenerate(msg: BridgeMessage): Promise<unknown> {
    const initialResponse = (await this.proxyToBackend(msg)) as {
      requestId?: string;
      status?: string;
      [key: string]: unknown;
    };
    if (initialResponse?.status !== 'pending' || typeof initialResponse.requestId !== 'string') {
      return initialResponse;
    }
    const requestId = initialResponse.requestId;
    const ws = getWebSocket(WEBSOCKET_ENDPOINT);

    return new Promise<unknown>((resolve, reject) => {
      let settled = false;
      const finish = (cb: () => void): void => {
        if (settled) return;
        settled = true;
        const entry = this.pendingImageRequests.get(requestId);
        if (entry) {
          entry.cleanup();
          this.pendingImageRequests.delete(requestId);
        }
        cb();
      };

      const unsubComplete = ws.onImageComplete((data) => {
        if (data.requestId !== requestId) return;
        // Reconstruct the pre-refactor `ImageInvokeResult` shape
        // byte-for-byte. The broadcast carries broadcast-only metadata
        // (`type`, `requestId`) that the SDK contract did not include
        // — stripping them keeps the iframe-bound payload identical to
        // what the synchronous HTTP response returned pre-refactor.
        const payload: Record<string, unknown> = {
          imageUrl: data.imageUrl,
          imageRef: data.imageRef,
          fileId: data.fileId,
          revisedPrompt: data.revisedPrompt,
          provider: data.provider,
          model: data.model,
          costCents: data.costCents,
        };
        if (data.deprecation) payload.deprecation = data.deprecation;
        finish(() => resolve(payload));
      });

      const unsubError = ws.onError((code, message, errRequestId) => {
        // Only fail THIS entry. Generic connection errors (no requestId
        // — e.g. CONNECTION_FAILED after max reconnect attempts) reach
        // every listener; per-request errors broadcast by the server
        // (e.g. image_generate failures) carry the originating
        // requestId via the extended `ErrorCallback` signature. Both
        // cases land here; the filter below routes them correctly:
        //   - requestId === ours → reject this pending image entry
        //   - requestId undefined → treat as a connection-level error
        //     and reject every pending entry (callers can retry)
        //   - requestId for a different request → ignore
        if (errRequestId !== undefined && errRequestId !== requestId) return;
        finish(() => reject(new Error(`${code}: ${message}`)));
      });

      // Escalating poll fallback. The WS `image_complete` (live or replayed
      // on subscribe) normally settles first and cancels this; if the
      // broadcast was missed, the poll recovers the durable result in seconds.
      const cancelPoll = this.startPollFallback(
        () =>
          this.pollImageStatus(requestId).then((s) =>
            s.kind === 'completed' ? { kind: 'completed' as const, value: s.payload } : s,
          ),
        (outcome) => {
          if (outcome.type === 'completed') {
            finish(() => resolve(outcome.value));
          } else if (outcome.type === 'failed') {
            finish(() => reject(outcome.error));
          } else {
            finish(() => reject(new Error('image_generate_timeout')));
          }
        },
      );

      const cleanup = (): void => {
        unsubComplete();
        unsubError();
        cancelPoll();
        // Unsubscribe THIS request only. Parameterless `unsubscribe()`
        // would clear every subscription on the shared singleton —
        // including a sibling internal-arche generation in another tab
        // and any other pending plugin image_generate. The targeted
        // form leaves those untouched.
        ws.unsubscribe(requestId);
      };

      this.pendingImageRequests.set(requestId, { resolve, reject, cleanup });

      // Best-effort WS subscribe for the fast path, AFTER the poll fallback is
      // already armed. Detached on purpose: a slow or wedged connect (e.g. the
      // socket is mid-reconnect after a page reload) must NEVER block the poll
      // — that's the durable safety net. See subscribeForAsyncResult.
      void this.subscribeForAsyncResult(ws, requestId);
    });
  }

  /**
   * Best-effort WebSocket subscribe for an async (`image_generate` /
   * `surface_invoke`) result. Runs DETACHED from the request promise so the
   * escalating poll fallback is never gated behind the WebSocket: if the
   * socket is mid-reconnect, the connect hangs, or the ticket fetch fails,
   * the poll still recovers the result from the server's durable
   * `request_state`. On success this is the fast path — the live
   * `image_complete` / `surface_complete` broadcast, or the replay the WS
   * service sends when this (possibly late) subscribe lands after completion,
   * resolves the pending request before the poll's grace window elapses.
   */
  private async subscribeForAsyncResult(
    ws: ReturnType<typeof getWebSocket>,
    requestId: string,
  ): Promise<void> {
    try {
      if (ws.getState() !== 'connected') {
        await ws.connect(() => authApi.getWebSocketTicket());
      }
      // The connect above can await a slow / mid-reconnect socket. If the
      // request already settled (success / error / timeout) or the host was
      // destroyed while we waited, its cleanup() has already run
      // ws.unsubscribe(requestId) — re-subscribing now would orphan a
      // subscription on the process-wide singleton that nothing ever removes
      // (and that re-sends on every reconnect). Only subscribe if the request
      // is still pending. requestIds are unique across both maps.
      if (
        !this.pendingImageRequests.has(requestId) &&
        !this.pendingSurfaceRequests.has(requestId)
      ) {
        return;
      }
      ws.subscribe(requestId);
    } catch {
      // Non-fatal: the poll fallback owns delivery when the WS can't.
    }
  }

  /**
   * Polls `GET /v1/plugins/image-status/:requestId` once. Returns a
   * discriminated union mirroring the server-side 4-state contract.
   * Called only from the 180 s safety-timer path; the happy path
   * resolves via WebSocket.
   */
  private async pollImageStatus(
    requestId: string,
  ): Promise<
    | { kind: 'pending' }
    | { kind: 'completed'; payload: unknown }
    | { kind: 'failed'; code: string; message: string }
    | { kind: 'not_found' }
  > {
    try {
      // Authenticate with the HOST's browser SESSION (the `fias_session`
      // cookie), NOT `getAuthToken()`. `getAuthToken` returns the plugin
      // BRIDGE capability token (for `/bridge/:archId`), but `/plugins/
      // image-status` is `requireAuth` (session) — sending the bridge token
      // there 401s, which silently degrades every poll to `pending` and is
      // why the fallback never recovered a missed broadcast. `credentials:
      // 'include'` matches how the rest of the web app calls session routes.
      const resp = await fetch(
        `${this.options.bridgeApiUrl}/plugins/image-status/${encodeURIComponent(requestId)}`,
        { credentials: 'include' },
      );
      if (resp.status === 404) return { kind: 'not_found' };
      const body = (await resp.json()) as {
        status?: string;
        payload?: unknown;
        error?: { code?: string; message?: string };
      };
      if (body.status === 'completed') return { kind: 'completed', payload: body.payload };
      if (body.status === 'failed') {
        return {
          kind: 'failed',
          code: body.error?.code ?? 'GENERATION_FAILED',
          message: body.error?.message ?? 'Image generation failed',
        };
      }
      return { kind: 'pending' };
    } catch {
      return { kind: 'pending' };
    }
  }

  /**
   * Async-via-WebSocket dispatch for `surface_invoke`. Generalizes
   * `handleAsyncImageGenerate` to any surface declaring `asyncDispatch`.
   *
   * Steps mirror the image path:
   *   1. POST the kickoff. An async surface returns `{ requestId, status:
   *      'pending' }`; a synchronous surface returns its full result —
   *      detected here and passed straight through (no WS wait).
   *   2. Subscribe to the shared FiasWebSocket for that requestId.
   *   3. Resolve on the `surface_complete` broadcast for THIS requestId
   *      with the opaque `result` (each surface's own `resultSchema`).
   *   4. 180s safety timer → single `surface-status` poll fallback.
   *   5. Cleanup on every terminal path (success/error/timeout/destroy).
   */
  private async handleAsyncSurfaceInvoke(msg: BridgeMessage): Promise<unknown> {
    const initialResponse = (await this.proxyToBackend(msg)) as {
      requestId?: string;
      status?: string;
      [key: string]: unknown;
    };
    if (initialResponse?.status !== 'pending' || typeof initialResponse.requestId !== 'string') {
      return initialResponse;
    }
    const requestId = initialResponse.requestId;
    const ws = getWebSocket(WEBSOCKET_ENDPOINT);

    return new Promise<unknown>((resolve, reject) => {
      let settled = false;
      const finish = (cb: () => void): void => {
        if (settled) return;
        settled = true;
        const entry = this.pendingSurfaceRequests.get(requestId);
        if (entry) {
          entry.cleanup();
          this.pendingSurfaceRequests.delete(requestId);
        }
        cb();
      };

      const unsubComplete = ws.onSurfaceComplete((data) => {
        if (data.requestId !== requestId) return;
        finish(() => resolve(data.result));
      });

      const unsubError = ws.onError((code, message, errRequestId) => {
        // Per-request errors carry the originating requestId; connection-
        // level errors (no requestId) fail every pending entry. Ignore
        // errors tagged for a different request.
        if (errRequestId !== undefined && errRequestId !== requestId) return;
        finish(() => reject(new Error(`${code}: ${message}`)));
      });

      // Escalating poll fallback (same contract as the image path). The WS
      // `surface_complete` (live or replayed on subscribe) normally settles
      // first and cancels this.
      const cancelPoll = this.startPollFallback(
        () =>
          this.pollSurfaceStatus(requestId).then((s) =>
            s.kind === 'completed' ? { kind: 'completed' as const, value: s.result } : s,
          ),
        (outcome) => {
          if (outcome.type === 'completed') {
            finish(() => resolve(outcome.value));
          } else if (outcome.type === 'failed') {
            finish(() => reject(outcome.error));
          } else {
            finish(() => reject(new Error('surface_invoke_timeout')));
          }
        },
      );

      const cleanup = (): void => {
        unsubComplete();
        unsubError();
        cancelPoll();
        ws.unsubscribe(requestId);
      };

      this.pendingSurfaceRequests.set(requestId, { resolve, reject, cleanup });

      // Best-effort WS subscribe, detached so it can't block the poll. See
      // subscribeForAsyncResult (shared with the image path).
      void this.subscribeForAsyncResult(ws, requestId);
    });
  }

  /**
   * Polls `GET /v1/plugins/surface-status/:requestId` once. Mirrors
   * `pollImageStatus`; called only from the 180s safety-timer path.
   */
  private async pollSurfaceStatus(
    requestId: string,
  ): Promise<
    | { kind: 'pending' }
    | { kind: 'completed'; result: unknown }
    | { kind: 'failed'; code: string; message: string }
    | { kind: 'not_found' }
  > {
    try {
      // Session-cookie auth (see pollImageStatus): `/plugins/surface-status`
      // is `requireAuth`, so the host's browser session — not the bridge
      // capability token from `getAuthToken()` — is what authenticates here.
      const resp = await fetch(
        `${this.options.bridgeApiUrl}/plugins/surface-status/${encodeURIComponent(requestId)}`,
        { credentials: 'include' },
      );
      if (resp.status === 404) return { kind: 'not_found' };
      const body = (await resp.json()) as {
        status?: string;
        result?: unknown;
        error?: { code?: string; message?: string };
      };
      if (body.status === 'completed') return { kind: 'completed', result: body.result };
      if (body.status === 'failed') {
        return {
          kind: 'failed',
          code: body.error?.code ?? 'SURFACE_INVOCATION_FAILED',
          message: body.error?.message ?? 'Surface invocation failed',
        };
      }
      return { kind: 'pending' };
    } catch {
      return { kind: 'pending' };
    }
  }

  private proxyToBackend(msg: BridgeMessage): Promise<unknown> {
    return this.callBackendOp(msg.type, msg.payload);
  }

  /**
   * Bridge endpoint for an op. Image-bearing `entity_invoke` goes to the
   * dedicated `/bridge-vision` route (a higher, isolated body limit for inline
   * image bytes); everything else uses the 256 KB `/bridge` route.
   */
  private bridgeEndpoint(type: string, payload: unknown): string {
    const images = (payload as { images?: unknown })?.images;
    const hasImages = type === 'entity_invoke' && Array.isArray(images) && images.length > 0;
    const path = hasImages ? 'bridge-vision' : 'bridge';
    return `${this.options.bridgeApiUrl}/plugins/${path}/${this.options.archId}`;
  }

  /** POST a single bridge op to the backend from the (unrestricted) host. */
  private async callBackendOp(type: string, payload: unknown, isRetry = false): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(this.bridgeEndpoint(type, payload), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ type, payload }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Bridge call failed' }));
      // One-shot recovery for a token that expired between retrieval and
      // server verification — rare (the token caches refresh 30s early), but
      // reachable in a long-lived preview under clock drift or a paused tab.
      // The hook busts the caller's token cache so the retried getAuthToken()
      // mints fresh instead of reading the same stale token back. Strictly
      // BRIDGE_TOKEN_EXPIRED: other 401/403s (invalid token, permission
      // denied, arche mismatch) are never retried.
      if (
        !isRetry &&
        (err as { error?: { code?: string } }).error?.code === 'BRIDGE_TOKEN_EXPIRED'
      ) {
        this.options.onAuthTokenExpired?.();
        return this.callBackendOp(type, payload, true);
      }
      // HostBridgeError preserves the server's error code both for host-side
      // callers that classify failures (the subscription manager's
      // terminal-vs-transient renewal logic) and for the iframe-bound error
      // response (handleMessage forwards `code` as `errorCode`).
      throw new HostBridgeError(
        err.error?.message ?? 'Bridge call failed',
        (err as { error?: { code?: string } }).error?.code ?? 'BRIDGE_ERROR',
      );
    }

    return response.json();
  }

  /**
   * Host-orchestrated binary upload (see the `vault_documents_upload` case).
   * Runs init → presigned PUT → finalize in the host context so the iframe's
   * `connect-src 'none'` CSP never blocks it. The presigned `uploadUrl` is
   * returned by the backend (init), so the iframe can't redirect the PUT
   * (no SSRF). Returns the finalize result (`{ documentId, status }`).
   */
  private async handleVaultUpload(payload: unknown): Promise<unknown> {
    const p = (payload ?? {}) as {
      name?: unknown;
      mimeType?: unknown;
      bytes?: unknown; // base64
      tags?: unknown;
      sensitivity?: unknown;
      folderPath?: unknown;
      replacesDocumentId?: unknown;
    };
    if (
      typeof p.name !== 'string' ||
      typeof p.mimeType !== 'string' ||
      typeof p.bytes !== 'string'
    ) {
      throw new Error('vault_documents_upload: name, mimeType, and bytes (base64) are required');
    }
    const u8 = base64ToBytes(p.bytes);
    const digest = await crypto.subtle.digest('SHA-256', u8);
    const checksumSha256 = bytesToHex(new Uint8Array(digest));

    const init = (await this.callBackendOp('vault_documents_upload_init', {
      name: p.name,
      mimeType: p.mimeType,
      sizeBytes: u8.byteLength,
      checksumSha256,
      tags: p.tags,
      sensitivity: p.sensitivity,
      folderPath: p.folderPath,
      replacesDocumentId: p.replacesDocumentId,
    })) as { documentId: string; uploadUrl: string; requiredHeaders: Record<string, string> };

    // Browsers forbid setting Content-Length explicitly; strip it.
    const headers = Object.fromEntries(
      Object.entries(init.requiredHeaders ?? {}).filter(
        ([k]) => k.toLowerCase() !== 'content-length',
      ),
    );
    const putResp = await fetch(init.uploadUrl, { method: 'PUT', body: u8, headers });
    if (!putResp.ok) {
      const text = await putResp.text().catch(() => '');
      throw new Error(`Vault upload PUT failed (${putResp.status}): ${text || putResp.statusText}`);
    }

    return this.callBackendOp('vault_documents_upload_finalize', {
      documentId: init.documentId,
      checksumSha256,
    });
  }

  /**
   * Shared store (IAP) handling for BOTH the legacy `store_*` alias ops and the
   * generic `entity_invoke` path (ADR-012 Phase L, Job 3). Reproduces the store
   * family's host-mediated behavior independent of transport: preview
   * simulation, the purchase-confirmation overlay (which gates a charge
   * client-side before any backend call), and the legacy wire SHAPES (bare
   * arrays / `{ success, ... }`). `proxy` performs the real backend call for a
   * store verb and MUST return the executor result already unwrapped to the
   * legacy shape — the alias transport returns it bare; the `entity_invoke`
   * transport unwraps the `{ output }` envelope first — so this method never
   * sees the envelope. Backend rejections propagate (throw) exactly as before.
   */
  private async handleStoreOp(
    op: string,
    args: Record<string, unknown>,
    proxy: (op: string, args: Record<string, unknown>) => Promise<unknown>,
  ): Promise<unknown> {
    if (this.options.previewMode) {
      // if/else (not a switch) on purpose: `case '<verb>':` labels here would be
      // picked up by the bridge-op-freeze / permission-coverage source scanners
      // as if they were new bridge ops.
      if (op === 'get_products') {
        return this.options.previewStoreProducts ?? [];
      }
      if (op === 'purchase') {
        // Only products the preview store KNOWS (the manifest's declared
        // products, passed as previewStoreProducts) can be "purchased" —
        // and the result is explicitly simulated. The old stub succeeded
        // unconditionally: a purchase of a nonexistent product "worked"
        // with no confirmation sheet, the same fake-success preview lie as
        // the old vault stubs (kitchen-sink finding, 2026-07-28).
        const { productIdentifier } = args as { productIdentifier?: string };
        const product = (this.options.previewStoreProducts ?? []).find(
          (p) => p.productIdentifier === productIdentifier,
        );
        if (!product) {
          return {
            success: false,
            code: 'PRODUCT_NOT_FOUND',
            error: `Product "${String(productIdentifier)}" is not declared in fias-plugin.json products — the preview store only knows declared products.`,
          };
        }
        return {
          success: true,
          // Marks the purchase as a preview simulation: no credits moved, no
          // entitlement persisted. Additive field; published results never
          // carry it.
          simulated: true,
          purchaseId: `preview_apc_${Date.now()}`,
          product: {
            productIdentifier: product.productIdentifier,
            productType: product.type,
            name: product.name,
          },
        };
      }
      if (op === 'get_entitlements' || op === 'restore') {
        return [];
      }
      if (op === 'get_purchase_history') {
        return { purchases: [] };
      }
      return { success: true };
    }
    // Published mode: the purchase-confirmation overlay gates `purchase`
    // client-side BEFORE any charge (unchanged from the legacy alias path).
    if (op === 'purchase' && this.options.onPurchaseRequest) {
      const { productIdentifier } = args as { productIdentifier?: string };
      const products = (await proxy('get_products', {})) as Array<{
        productIdentifier: string;
        name: string;
        description: string | null;
        priceCredits: number;
        type: string;
      }>;
      const product = Array.isArray(products)
        ? products.find((p) => p.productIdentifier === productIdentifier)
        : undefined;
      if (!product) {
        return { success: false, error: 'Product not found' };
      }
      return this.options.onPurchaseRequest(product);
    }
    return proxy(op, args);
  }

  /**
   * Resolve the rate-limit bucket key + per-minute cap for a call. For
   * `entity_invoke` on a migrated op-family (ADR-012 Phase L), this is
   * per-(entityId, verb) so storage/datastore/workspaces/store keep their
   * distinct caps instead of collapsing into the single `entity_invoke` bucket.
   * Everything else (including AI/text `entity_invoke`, datastore search /
   * subscribe) keys by op type. Returns null when the op has no configured cap.
   */
  private resolveRateLimit(
    type: string,
    payload: unknown,
  ): { key: string; maxPerMinute: number; units: number } | null {
    if (type === 'entity_invoke') {
      const p = payload as {
        entityId?: unknown;
        payload?: { op?: unknown; operations?: unknown };
      } | null;
      const entityId = typeof p?.entityId === 'string' ? p.entityId : '';
      const op = typeof p?.payload?.op === 'string' ? p.payload.op : '';
      const perVerb = ENTITY_INVOKE_RATE_LIMITS[entityId]?.[op];
      if (perVerb !== undefined) {
        // ADR-016 Phase A: a weighted verb (see ENTITY_INVOKE_RATE_WEIGHTS)
        // consumes one unit per started opsPerUnit operations, so the 100-op
        // batch cap raise did not raise client-granted write throughput.
        const opsPerUnit = ENTITY_INVOKE_RATE_WEIGHTS[entityId]?.[op];
        const units =
          opsPerUnit !== undefined && Array.isArray(p?.payload?.operations)
            ? Math.max(1, Math.ceil(p.payload.operations.length / opsPerUnit))
            : 1;
        return { key: `entity_invoke:${entityId}:${op}`, maxPerMinute: perVerb, units };
      }
    }
    const limit = RATE_LIMITS[type];
    return limit ? { key: type, maxPerMinute: limit.maxPerMinute, units: 1 } : null;
  }

  private checkRateLimit(type: string, payload: unknown): void {
    const resolved = this.resolveRateLimit(type, payload);
    // Burst detection first (catches runaway loops in <5s) — keyed the same way
    // so per-verb entity_invoke calls get independent burst windows too.
    this.checkBurst(resolved?.key ?? type, type, payload);

    if (!resolved) return;

    const now = Date.now();
    const bucket = this.rateLimits.get(resolved.key);

    if (!bucket || now - bucket.windowStart > 60_000) {
      this.rateLimits.set(resolved.key, { count: resolved.units, windowStart: now });
      return;
    }

    // Weighted (ADR-016 Phase A) and all-or-nothing, mirroring the server: a
    // call that does not fit charges nothing, so an over-sized batch cannot
    // starve the caller's own remaining budget.
    if (bucket.count + resolved.units > resolved.maxPerMinute) {
      const target = describeBridgeTarget(type, payload);
      const targetSuffix = target ? ` (${target})` : '';
      throw new Error(
        `Rate limit exceeded for ${type}${targetSuffix}: max ${resolved.maxPerMinute}/minute`,
      );
    }

    bucket.count += resolved.units;
  }

  private checkBurst(bucketKey: string, displayType: string, payload: unknown): void {
    const now = Date.now();
    const bucket = this.burstBuckets.get(bucketKey);
    const target = describeBridgeTarget(displayType, payload);
    const targetSuffix = target ? ` (${target})` : '';

    // If currently in cooldown, block silently (onLog already fired on first breach)
    if (bucket?.blockedUntil && now < bucket.blockedUntil) {
      throw new BridgeBurstError(
        `Bridge call "${displayType}"${targetSuffix} temporarily blocked — rapid-fire loop detected. ` +
          `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`,
      );
    }

    // Reset expired burst windows
    if (!bucket || now - bucket.windowStart > BURST_WINDOW_MS) {
      this.burstBuckets.set(bucketKey, { count: 1, windowStart: now });
      return;
    }

    bucket.count++;

    if (bucket.count > BURST_THRESHOLD) {
      bucket.blockedUntil = now + BURST_COOLDOWN_MS;
      const msg =
        `Runaway loop detected: "${displayType}"${targetSuffix} called ${bucket.count} times in ${BURST_WINDOW_MS / 1000}s. ` +
        `Calls blocked for ${BURST_COOLDOWN_MS / 1000}s. ` +
        `Check useEffect dependency arrays, or debounce writes if this is a usePersistentState key being updated at high frequency.`;
      this.options.onLog?.('error', msg);
      throw new BridgeBurstError(
        `Bridge call "${displayType}"${targetSuffix} temporarily blocked — rapid-fire loop detected.`,
      );
    }
  }

  private sendToPlugin(message: BridgeMessage): void {
    // targetOrigin '*' is required because sandboxed iframes without allow-same-origin
    // have an opaque (null) origin. Security is enforced by checking event.source
    // against iframe.contentWindow on incoming messages (line 113).
    this.options.iframe.contentWindow?.postMessage(message, '*');
  }

  private sendResponse(messageId: string, payload: unknown): void {
    this.sendToPlugin({
      type: 'response' as string,
      messageId,
      payload,
    });
  }

  private sendError(messageId: string, error: string, errorCode?: string): void {
    // See sendToPlugin() for why '*' is used as targetOrigin. `errorCode` is a
    // plain wire field: the SDK constructs a FiasBridgeError from
    // `error` + `errorCode` locally (never structured-clones an Error).
    this.options.iframe.contentWindow?.postMessage(
      {
        type: 'response',
        messageId,
        payload: null,
        error,
        ...(errorCode !== undefined ? { errorCode } : {}),
      },
      '*',
    );
  }
}

// ── Binary helpers for the host-orchestrated vault upload ────────────────────

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, '0');
  return out;
}
